/* DRIVER'S LOG — Jay's Apps Script app moved into the Portal (Jay, 2026-10-19; Max: "chuyển qua làm này nhé").

   TWO SCREENS IN ONE ROUTE. A driver opens /driver-log on a phone and gets the day, full screen, nothing else: Jay asked
   for "phones in portrait mode … big and easy to tap … someone new should be able to use it without explanation …
   keep the steps to enter a day as few as possible". So the day opens on TODAY, the start km is already the last end
   km, the first leg already starts where the car stopped last, and Save is always at the bottom of the thumb. An
   admin gets the six tabs of Jay's admin screen inside the Portal's own frame, plus "Driver view" to see exactly what
   a driver sees.

   VIETNAMESE OR ENGLISH, the app's own switch, as Jay's app had: the drivers read Vietnamese and the Portal's own
   languages are English and Korean. The choice is remembered on the device. */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, LogOut, X, Eye, MapPin, Loader2, Lock, Save, FileText, Download } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import './SPHub.css';
import './ProjectApproval.css';
import './DriverLog.css';

const API = `${BACKEND_URL}/api/driver-log`;
const call = async (url, method = 'GET', body) => {
  const r = await fetch(url, {
    method, credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

/* ---- the app's own words, Vietnamese and English (Jay's dictionary, kept where it fits) ---- */
const T = {
  vi: {
    tab_report: 'Báo cáo', rp_title: 'Báo cáo theo tháng', rp_hint: 'Tích chọn các tháng muốn xem (chọn được nhiều năm) hoặc bấm một mốc nhanh. Số liệu tính từ nhật ký từng ngày, tháng đang mở cũng xem được.',
    rp_quick: 'Chọn nhanh', rp_last: 'Tháng trước', rp_l3: '3 tháng gần nhất', rp_l6: '6 tháng gần nhất', rp_l12: '12 tháng gần nhất',
    rp_h1: '6 tháng đầu {0}', rp_h2: '6 tháng cuối {0}', rp_year: 'Cả năm {0}', rp_clear: 'Bỏ chọn', rp_picked: 'Đã chọn {0} tháng',
    rp_all_cars: 'Tất cả xe', rp_km: 'Km đã đi', rp_fuel: 'Tiền xăng', rp_vpk: 'Tiền xăng / km', rp_gas: 'Giá xăng VN', rp_gas_date: 'Ngày giá',
    rp_month: 'Tháng', rp_total: 'TỔNG', rp_pick_some: 'Chọn ít nhất một tháng để xem báo cáo.', rp_pdf: 'Tải PDF', rp_nolog: 'không có nhật ký',
    rp_typed: 'Giá nhập tay (từ báo cáo GW của chị Huệ hoặc tab Giá xăng)', rp_car: '{0} — xe của {1}', rp_chart: 'Tiền xăng / km theo tháng',
    rp_period: 'Kỳ: {0}', rp_mon: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'],
    title: 'Nhật ký lái xe', logout: 'Thoát', today: 'Hôm nay', notToday: 'Về hôm nay',
    time: 'Thời gian', start_time: 'Giờ bắt đầu', end_time: 'Giờ kết thúc',
    start_hint: 'Thường là 6:00. Chỉ chọn sớm hơn khi có chuyến phải đi sớm.', lunch_calc: 'Tăng ca trưa: {0}',
    lunch_legacy: 'Đã ghi trước đây: {0} tăng ca trưa (chưa có khung giờ).', lunch_half: 'Giờ nghỉ trưa: chọn đủ giờ bắt đầu và giờ kết thúc.',
    lunch_order: 'Giờ nghỉ trưa: giờ kết thúc phải sau giờ bắt đầu.', clear_time: 'Xóa',
    odo: 'Số km (đồng hồ)', start_km: 'Km bắt đầu', end_km: 'Km kết thúc', prev_km: 'Km kết thúc lần trước: {0} ({1})',
    moved: 'Đã đi: {0} km', route: 'Hành trình', leg: 'Chặng {0}', from: 'Từ', to: 'Đến', add_trip: '+ Thêm chặng',
    costs: 'Chi phí', fuel: 'Tiền xăng (VND)', other: 'Chi phí khác (VND)', other_note: 'Ghi chú chi phí khác',
    other_note_ph: 'VD: phí đỗ xe, bảo dưỡng...', save: 'Lưu', saving: 'Đang lưu…',
    choose: '— chọn —', new_place_opt: '+ Địa điểm mới…', new_tag: ' (mới)', remove: 'Xóa',
    np_title: 'Thêm địa điểm mới', place_name: 'Tên địa điểm', addr_opt: 'Địa chỉ (không bắt buộc)', cancel: 'Hủy', add: 'Thêm',
    saved_km: 'Đã lưu. Đi {0} km.', saved_warn: 'Đã lưu. Lưu ý: ', locked: 'Tháng này đã chốt. Không sửa được nữa.',
    locked_admin: ' Mở lại ở tab "Chốt tháng".', exists: 'Ngày này đã lưu lúc {0}. Có thể sửa và lưu lại.', fresh: 'Chưa nhập ngày này.',
    loading: 'Đang tải…', need_place_name: 'Nhập tên địa điểm.', no_car: 'Tài khoản này chưa được gán xe. Báo admin (Hue).',
    car: 'Xe', driver: 'Tài xế', weekday: ['CN', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'],
    // admin
    admin: 'Quản trị', tab_overview: 'Tổng quan', tab_entry: 'Nhập liệu', tab_places: 'Địa điểm', tab_people: 'Tài xế & Xe',
    tab_gas: 'Giá xăng', tab_close: 'Chốt tháng', preview_btn: 'Xem như tài xế', pv_label: 'Đang xem màn hình tài xế:',
    pv_note: 'Dữ liệu lưu ở đây là dữ liệu thật.', pv_exit: '← Quay lại quản trị', month: 'Tháng',
    days_logged: 'Số ngày', km: 'Km', other_short: 'Khác (VND)', th_missing: 'Ngày thiếu', not_logged: 'Chưa nhập',
    weekend: 'Cuối tuần', total: 'Tổng', total_days: '{0} ngày có nhật ký / {1} ngày trong tháng', missing: 'Ngày làm việc chưa nhập: ', none_missing: 'Không thiếu ngày nào.',
    th_date: 'Ngày', th_time: 'Giờ', th_route: 'Hành trình', th_start: 'Km đầu', th_end: 'Km cuối', th_fuel: 'Xăng', th_other: 'Khác',
    th_note: 'Ghi chú', th_by: 'Người nhập', no_data: 'Chưa có dữ liệu.', edit_car: '+ Nhập/sửa cho xe này', open: 'Đang mở',
    closed: 'Đã chốt', reopened: 'Mở lại', st_pending: 'Chờ duyệt', st_active: 'Đang dùng', st_inactive: 'Ngừng dùng',
    pl_new: '+ Thêm địa điểm', th_name: 'Tên', th_status: 'Trạng thái', th_addr: 'Địa chỉ', th_uses: 'Số lần dùng', th_map: 'Bản đồ',
    open_map: 'Mở', no_coord: 'Chưa có', edit: 'Sửa', pe_edit: 'Sửa địa điểm', pe_new: 'Thêm địa điểm', pe_search: 'Tìm vị trí',
    pe_search_ph: 'Dán link Google Maps, tọa độ, hoặc gõ địa chỉ', find: 'Tìm',
    pe_help: 'Nhanh nhất: mở Google Maps → tìm địa điểm → Chia sẻ → Sao chép đường liên kết → dán vào ô trên → Tìm.',
    pe_coord: 'Tọa độ (tự điền)', searching: 'Đang tìm…', not_found: 'Không tìm thấy. Thử dán link Google Maps.',
    drivers_link: 'Link gửi tài xế', drivers_link_how: 'mở trên điện thoại → Đăng nhập bằng Google → chọn đúng Gmail ghi ở bảng dưới. Chỉ một lần, giữ đăng nhập 30 ngày.',
    drivers: 'Tài xế', cars: 'Xe', th_active: 'Đang dùng', new_driver: '+ Tài xế mới', new_car: '+ Xe mới', th_plate: 'Biển số',
    th_model: 'Dòng xe', th_year: 'Năm', th_odo: 'Số km hiện tại', th_driver: 'Tài xế', th_account: 'Tài khoản Portal',
    no_account: '— chưa gán —', th_email: 'Email Google của tài xế',
    email_hint: 'Gmail cá nhân (hoặc @helinox.com) tài xế dùng để đăng nhập Google. Tài khoản Portal đi theo email này.',
    acct_found: 'Tài khoản Portal: {0} — gắn vào tài xế khi lưu.',
    acct_none: 'Chưa có tài khoản Portal — lần đăng nhập Google đầu tiên tự tạo (role Driver) và gắn vào đây.',
    status_lbl: 'Trạng thái', fp_clear: 'Bỏ lọc', rows_of: '{0} / {1} dòng',
    close_before: 'Nhật ký lái xe bắt đầu từ tháng {1}, tháng {0} không có gì để chốt.',
    close_not_ended: 'Tháng {0} chưa hết — chốt được từ ngày 1 tháng sau.',
    reopen_none: 'Tháng {0} chưa có xe nào đang chốt.',
    close_body1: 'Portal tạo PDF nhật ký tháng {0} cho từng xe đang dùng hoặc có ngày trong tháng.',
    close_body2: 'Gửi mail kèm PDF tới: {0}.',
    close_body3: 'Sau khi chốt, tài xế không sửa được ngày trong tháng này. Cần sửa thì bấm Mở lại tháng.',
    reopen_body1: 'Tài xế sửa lại được các ngày trong tháng {0}.',
    reopen_body2: 'PDF đã tạo vẫn giữ. Sửa xong bấm Chốt tháng lần nữa để tạo PDF mới.',
    close_t: 'Chốt tháng {0}', reopen_t: 'Mở lại tháng {0}',
    acct_hint: 'Tài khoản gắn ở đây chỉ thấy màn hình tài xế, dù đang có role gì. Riêng admin của Nhật ký lái xe vẫn giữ toàn bộ Portal.',
    acct_roles: 'Sau khi đăng nhập thấy', not_signed_in: 'Chưa đăng nhập lần nào', only_driver: 'Chỉ màn hình tài xế',
    keeps_portal: 'Toàn bộ Portal (admin)', gas_title: 'Giá xăng theo tháng', gas_price: 'Giá (VND/lít)', ref_date: 'Ngày tham chiếu',
    gas_save: 'Lưu giá xăng', close_title: 'Chốt / mở lại tháng',
    gas_auto_title: 'Giá xăng tự động', gas_grade: 'Loại xăng', gas_zone: 'Vùng {0}', gas_current: 'Giá hiện hành',
    gas_since: 'từ ngày {0}', gas_last_sync: 'Lấy giá lần cuối',
    gas_rule: 'Lấy giá Petrolimex từng ngày từ giaxanghomnay.com, 3 giờ một lần. Giá tháng = bình quân các ngày; giá nhập tay được ưu tiên.',
    gas_new: '+ Nhập giá tháng', gas_title_one: 'Giá xăng của một tháng', new_driver_t: 'Tài xế mới', edit_driver: 'Sửa tài xế',
    new_car_t: 'Xe mới', edit_car_t: 'Sửa xe',
    gas_sync: 'Cập nhật giá ngay', gas_synced: 'Đã cập nhật ({0} ngày). Giá hôm nay {1} VND/lít.',
    gas_alert_to: 'Nếu một nguồn lỗi 2 lần liên tiếp (khoảng 3 giờ) hoặc hai nguồn lệch giá, Portal gửi email tới {0}, nhắc lại mỗi 24 giờ và báo khi chạy lại.',
    gas_failing_since: 'lỗi từ {0} ({1} lần liên tiếp)', gas_mailed: 'đã gửi email',
    gas_src_giaxanghomnay: 'giaxanghomnay.com', gas_src_petrolimex: 'Petrolimex', gas_src_mismatch: 'Hai nguồn lệch giá',
    gas_daily_title: 'Giá từng ngày ({0})',
    gas_no_grade: 'Đã lấy giá, nhưng Petrolimex không có loại xăng đang chọn.', gas_auto_back: 'Tháng {0} dùng lại giá tự động.',
    gas_type_hint: 'Chỉ nhập tay khi cần thay giá tự động của một tháng.', gas_source: 'Nguồn',
    gas_src_auto: 'Tự động · bình quân {0}/{1} ngày', gas_src_typed: 'Nhập tay', gas_use_auto: 'Dùng giá tự động',
    gas_log_title: 'Các lần đổi giá đã ghi ({0})', gas_changed_on: 'Ngày đổi giá',
    close_hint: 'Tháng trước tự chốt lúc 06:00 ngày 1. Dùng các nút này để chốt lại sau khi sửa.',
    btn_close: 'Chốt tháng (tạo PDF)', btn_reopen: 'Mở lại tháng', no_closings: 'Chưa chốt tháng nào.',
    ask_close: 'Chốt tháng {0} cho tất cả xe? PDF sẽ được tạo và gửi mail.', ask_reopen: 'Mở lại tháng {0}? Tài xế sửa được lại.',
    before_start: 'Nhật ký lái xe bắt đầu từ tháng {0}, tháng này không tính ngày chưa nhập.',
    closed_msg: 'Đã chốt {0}', reopened_msg: 'Đã mở lại {0}', pdf: 'PDF', vnd_per_km: 'VND/km', gas_short: 'Giá xăng',
    saved: 'Đã lưu', active: 'Đang dùng', yes: 'Có', no: 'Không',
    ot: 'Tăng ca', ot_lunch: 'Làm trong giờ nghỉ trưa (11:00–12:00)', ot_evening: 'Tăng ca tối (sau 18:00)', ot_other: 'Tăng ca cuối tuần',
    ot_morning: 'Tăng ca sáng (trước 6:00)', ot_weekend: 'Tăng ca cuối tuần (cả ngày)', ot_morning_s: 'Sáng',
    ot_rule: 'Giờ làm 6:00–18:00. Tăng ca sáng, tối, cuối tuần và cơm tối tự tính theo giờ bắt đầu, kết thúc.',
    ot_bad_times: 'Giờ kết thúc đang trước giờ bắt đầu — kiểm tra lại giờ.', ot_lunch_hint: 'Có làm trong giờ nghỉ trưa thì chọn từ mấy giờ đến mấy giờ. Không làm thì để trống.',
    ot_kept: 'Ngày nhập từ file cũ: giữ tăng ca đã ghi, trừ khi đổi giờ.',
    other_costs: 'Chi phí khác', cost_type: 'Loại chi phí', cost_choose: 'Chọn loại…', cost_new_opt: '+ Thêm loại khác…',
    cost_new_ph: 'Tên loại chi phí mới', cost_amount: 'Số tiền (VND)', cost_note: 'Ghi chú (không bắt buộc)',
    add_cost: '+ Thêm chi phí khác', need_cost_type: 'Chọn loại cho mỗi chi phí khác.', untyped: '(chưa có loại)', cost_line: 'Chi phí {0}',
    ot_auto: 'Tự tính từ giờ kết thúc — sửa nếu khác.', dinner: 'Phụ cấp cơm tối', dinner_hint: 'Tự đánh dấu khi về sau 19:00.',
    hours: 'giờ', minutes: 'phút', remark: 'Ghi chú trong ngày', remark_ph: 'VD: chở các sếp đi nhà máy, đón khách sân bay…',
    typed_tag: '(chữ gõ)', imported_from: 'Ngày này nhập từ file cũ của chị Huệ: {0}.', th_ot: 'Tăng ca', th_dinner: 'Cơm tối',
    ot_lunch_s: 'Trưa', ot_evening_s: 'Tối', ot_other_s: 'Cuối tuần', dinner_days: '{0} ngày',
    vnd_l: 'VND/lít', email_ph: 'ten@gmail.com',
  },
  en: {
    tab_report: 'Report', rp_title: 'Monthly report', rp_hint: 'Tick the months you want (across years too) or press a quick pick. Figures are worked out from the daily logs, so an open month shows as it stands.',
    rp_quick: 'Quick pick', rp_last: 'Last month', rp_l3: 'Last 3 months', rp_l6: 'Last 6 months', rp_l12: 'Last 12 months',
    rp_h1: 'First half {0}', rp_h2: 'Second half {0}', rp_year: 'Year {0}', rp_clear: 'Clear', rp_picked: '{0} month(s) picked',
    rp_all_cars: 'All cars', rp_km: 'KMs done', rp_fuel: 'Fuel paid', rp_vpk: 'Fuel per km', rp_gas: 'Vietnam gas price', rp_gas_date: 'Price date',
    rp_month: 'Month', rp_total: 'TOTAL', rp_pick_some: 'Pick at least one month to see the report.', rp_pdf: 'Download PDF', rp_nolog: 'no log',
    rp_typed: 'Typed price (from Hue\'s GW report or the Gas price tab)', rp_car: '{0} — {1}\'s car', rp_chart: 'Fuel per km by month',
    rp_period: 'Period: {0}', rp_mon: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    title: "Driver's Log", logout: 'Sign out', today: 'Today', notToday: 'Back to today',
    time: 'Time', start_time: 'Start time', end_time: 'End time',
    start_hint: 'Usually 6:00. Pick earlier only when a trip needs an early start.', lunch_calc: 'Lunch overtime: {0}',
    lunch_legacy: 'Recorded earlier: {0} of lunch overtime (no time range).', lunch_half: 'Lunch break: pick both the start and the end.',
    lunch_order: 'Lunch break: the end must be after the start.', clear_time: 'Clear',
    odo: 'Odometer (km)', start_km: 'Start km', end_km: 'End km', prev_km: 'Last end km: {0} ({1})',
    moved: 'Driven: {0} km', route: 'Route', leg: 'Leg {0}', from: 'From', to: 'To', add_trip: '+ Add leg',
    costs: 'Costs', fuel: 'Fuel (VND)', other: 'Other cost (VND)', other_note: 'Other cost note',
    other_note_ph: 'e.g. parking, maintenance...', save: 'Save', saving: 'Saving…',
    choose: '— select —', new_place_opt: '+ New place…', new_tag: ' (new)', remove: 'Remove',
    np_title: 'Add new place', place_name: 'Place name', addr_opt: 'Address (optional)', cancel: 'Cancel', add: 'Add',
    saved_km: 'Saved. {0} km driven.', saved_warn: 'Saved. Note: ', locked: 'This month is closed. Editing is locked.',
    locked_admin: ' Reopen it in "Month close".', exists: 'This day was saved at {0}. You can edit and save again.', fresh: 'Nothing logged for this day yet.',
    loading: 'Loading…', need_place_name: 'Enter a place name.', no_car: 'No car is assigned to this account. Ask the admin (Hue).',
    car: 'Car', driver: 'Driver', weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    admin: 'Admin', tab_overview: 'Overview', tab_entry: 'Daily entry', tab_places: 'Places', tab_people: 'Drivers & Cars',
    tab_gas: 'Gas price', tab_close: 'Month close', preview_btn: 'Driver view', pv_label: 'Viewing as driver:',
    pv_note: 'Data saved here is real.', pv_exit: '← Back to admin', month: 'Month',
    days_logged: 'Days logged', km: 'Km', other_short: 'Other (VND)', th_missing: 'Days missing', not_logged: 'Not logged',
    weekend: 'Weekend', total: 'Total', total_days: '{0} days logged / {1} days in the month', missing: 'Weekdays without a log: ', none_missing: 'No day missing.',
    th_date: 'Date', th_time: 'Time', th_route: 'Route', th_start: 'Start km', th_end: 'End km', th_fuel: 'Fuel', th_other: 'Other',
    th_note: 'Note', th_by: 'Entered by', no_data: 'No data yet.', edit_car: '+ Enter/edit for this car', open: 'Open',
    closed: 'Closed', reopened: 'Reopened', st_pending: 'Pending', st_active: 'Active', st_inactive: 'Inactive',
    pl_new: '+ Add place', th_name: 'Name', th_status: 'Status', th_addr: 'Address', th_uses: 'Uses', th_map: 'Map',
    ot: 'Overtime', ot_lunch: 'Worked through the lunch break (11:00–12:00)', ot_evening: 'Evening overtime (after 18:00)', ot_other: 'Weekend overtime',
    ot_morning: 'Morning overtime (before 06:00)', ot_weekend: 'Weekend overtime (whole day)', ot_morning_s: 'Morn',
    ot_rule: 'Working hours 06:00–18:00. Morning, evening and weekend overtime and the dinner allowance come from the start and end times.',
    ot_bad_times: 'The end time is before the start time — check the times.', ot_lunch_hint: 'If you worked through the lunch break, pick from when to when. Leave it empty if not.',
    ot_kept: "Imported from the old log: its recorded overtime stays unless the times change.",
    other_costs: 'Other costs', cost_type: 'Kind of cost', cost_choose: 'Choose…', cost_new_opt: '+ Add another kind…',
    cost_new_ph: 'Name of the new kind', cost_amount: 'Amount (VND)', cost_note: 'Note (optional)',
    add_cost: '+ Add another cost', need_cost_type: 'Pick the kind of each other cost.', untyped: '(no kind)', cost_line: 'Cost {0}',
    ot_auto: 'Worked out from the end time — change it if different.', dinner: 'Dinner allowance', dinner_hint: 'Ticked by itself when the day ends after 19:00.',
    hours: 'h', minutes: 'min', remark: "Today's note", remark_ph: 'e.g. took the managers to the factory, airport pick-up…',
    typed_tag: '(as typed)', imported_from: 'This day was imported from Hue\'s old log: {0}.', th_ot: 'Overtime', th_dinner: 'Dinner',
    ot_lunch_s: 'Lunch', ot_evening_s: 'Eve', ot_other_s: 'Weekend', dinner_days: '{0} days',
    open_map: 'Open', no_coord: 'None', edit: 'Edit', pe_edit: 'Edit place', pe_new: 'Add place', pe_search: 'Find location',
    pe_search_ph: 'Paste a Google Maps link, coordinates, or type an address', find: 'Find',
    pe_help: 'Fastest: open Google Maps → find the place → Share → Copy link → paste above → Find.',
    pe_coord: 'Coordinates (auto-filled)', searching: 'Searching…', not_found: 'Not found. Try pasting a Google Maps link.',
    drivers_link: 'Link for the drivers', drivers_link_how: 'open it on the phone → Sign in with Google → pick the Gmail written below. Once only; it stays signed in for 30 days.',
    drivers: 'Drivers', cars: 'Cars', th_active: 'Active', new_driver: '+ New driver', new_car: '+ New car', th_plate: 'Plate',
    th_model: 'Model', th_year: 'Year', th_odo: 'Odometer', th_driver: 'Driver', th_account: 'Portal account',
    no_account: '— not linked —', th_email: "Driver's Google email",
    email_hint: 'The driver\'s own Gmail (or @helinox.com) address they sign in to Google with. The Portal account follows this address.',
    acct_found: 'Portal account: {0} — linked to the driver when you save.',
    acct_none: 'No Portal account yet — the first Google sign-in creates it (role Driver) and links it here.',
    status_lbl: 'Status', fp_clear: 'Clear filters', rows_of: '{0} of {1} rows',
    close_before: "The Driver's Log starts in {1}; {0} has nothing to close.",
    close_not_ended: '{0} has not ended — it can be closed from the 1st of the next month.',
    reopen_none: 'No car is closed for {0}.',
    close_body1: 'The Portal makes the {0} log PDF for every car in use or with a day in the month.',
    close_body2: 'The PDFs are mailed to: {0}.',
    close_body3: 'Once closed, drivers cannot change a day of this month. To correct one, reopen the month.',
    reopen_body1: 'Drivers can change the days of {0} again.',
    reopen_body2: 'The PDFs already made are kept. After the edits, close the month again for new PDFs.',
    close_t: 'Close {0}', reopen_t: 'Reopen {0}',
    acct_hint: "An account linked here sees the driver screen only, whatever roles it holds. A Driver's Log admin keeps the whole portal.",
    acct_roles: 'After sign-in sees', not_signed_in: 'Not signed in yet', only_driver: 'Driver screen only',
    keeps_portal: 'The whole portal (admin)', gas_title: 'Monthly gas price', gas_price: 'Price (VND/L)', ref_date: 'Reference date',
    gas_save: 'Save gas price', close_title: 'Close / reopen month',
    gas_auto_title: 'Automatic gas price', gas_grade: 'Fuel', gas_zone: 'Zone {0}', gas_current: 'Price in force',
    gas_since: 'since {0}', gas_last_sync: 'Last read',
    gas_rule: "Petrolimex's price for each day, read from giaxanghomnay.com every 3 hours. A month = the average of its days; a typed price wins.",
    gas_new: '+ Enter a month', gas_title_one: "A month's gas price", new_driver_t: 'New driver', edit_driver: 'Edit driver',
    new_car_t: 'New car', edit_car_t: 'Edit car',
    gas_sync: 'Update now', gas_synced: 'Updated ({0} day(s)). Today {1} VND/L.',
    gas_alert_to: 'If a source fails twice in a row (about 3 hours) or the two disagree, the Portal mails {0}, again every 24 hours, and once it works again.',
    gas_failing_since: 'failing since {0} ({1} runs in a row)', gas_mailed: 'mailed',
    gas_src_giaxanghomnay: 'giaxanghomnay.com', gas_src_petrolimex: 'Petrolimex', gas_src_mismatch: 'The two sources disagree',
    gas_daily_title: 'Price by day ({0})',
    gas_no_grade: 'Read, but Petrolimex lists no price for the chosen fuel.', gas_auto_back: '{0} uses the automatic price again.',
    gas_type_hint: "Type a price only to replace a month's automatic one.", gas_source: 'Source',
    gas_src_auto: 'Automatic · average of {0}/{1} days', gas_src_typed: 'Typed', gas_use_auto: 'Use the automatic price',
    gas_log_title: 'Price changes recorded ({0})', gas_changed_on: 'Changed on',
    close_hint: 'Last month closes automatically at 06:00 on the 1st. Use these buttons to re-close after edits.',
    btn_close: 'Close month (create PDF)', btn_reopen: 'Reopen month', no_closings: 'No closed months yet.',
    ask_close: 'Close {0} for all cars? PDFs will be created and emailed.', ask_reopen: 'Reopen {0}? Drivers can edit again.',
    before_start: "The Driver's Log starts in {0}; no day of this month counts as missing.",
    closed_msg: 'Closed {0}', reopened_msg: 'Reopened {0}', pdf: 'PDF', vnd_per_km: 'VND/km', gas_short: 'Gas price',
    saved: 'Saved', active: 'Active', yes: 'Yes', no: 'No',
    vnd_l: 'VND/L', email_ph: 'name@gmail.com',
  },
  /* Korean and Simplified Chinese for the ADMIN screens (2026-10-21), which follow the portal's language. A driver's own
     screen and the form inside "View as driver" stay Vietnamese whatever the portal reads (useLang below). */
  ko: {
    tab_report: '보고서', rp_title: '월별 보고서', rp_hint: '보고 싶은 달을 체크하거나(여러 해 가능) 빠른 선택을 누르세요. 수치는 일별 기록에서 계산되어 마감 전 달도 볼 수 있습니다.',
    rp_quick: '빠른 선택', rp_last: '지난달', rp_l3: '최근 3개월', rp_l6: '최근 6개월', rp_l12: '최근 12개월',
    rp_h1: '{0} 상반기', rp_h2: '{0} 하반기', rp_year: '{0}년 전체', rp_clear: '선택 해제', rp_picked: '{0}개월 선택',
    rp_all_cars: '전체 차량', rp_km: '주행 km', rp_fuel: '유류비', rp_vpk: 'km당 유류비', rp_gas: '베트남 유가', rp_gas_date: '유가 기준일',
    rp_month: '월', rp_total: '합계', rp_pick_some: '보고서를 보려면 한 달 이상 선택하세요.', rp_pdf: 'PDF 다운로드', rp_nolog: '기록 없음',
    rp_typed: '직접 입력한 유가(Hue의 GW 보고서 또는 유가 탭)', rp_car: '{0} — {1} 차량', rp_chart: '월별 km당 유류비',
    rp_period: '기간: {0}', rp_mon: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
    title: '운행 일지', logout: '로그아웃', today: '오늘', notToday: '오늘로 돌아가기',
    time: '시간', start_time: '시작 시간', end_time: '종료 시간',
    start_hint: '보통 6:00입니다. 일찍 출발해야 하는 운행이 있을 때만 더 이르게 선택하세요.', lunch_calc: '점심 초과근무: {0}',
    lunch_legacy: '이전 기록: 점심 초과근무 {0} (시간대 없음).', lunch_half: '점심시간: 시작과 종료를 모두 선택하세요.',
    lunch_order: '점심시간: 종료가 시작보다 늦어야 합니다.', clear_time: '지우기',
    odo: '주행거리계 (km)', start_km: '시작 km', end_km: '종료 km', prev_km: '지난번 종료 km: {0} ({1})',
    moved: '주행: {0} km', route: '경로', leg: '구간 {0}', from: '출발', to: '도착', add_trip: '+ 구간 추가',
    costs: '비용', fuel: '주유비 (VND)', other: '기타 비용 (VND)', other_note: '기타 비용 메모',
    other_note_ph: '예: 주차비, 정비...', save: '저장', saving: '저장 중…',
    choose: '— 선택 —', new_place_opt: '+ 새 장소…', new_tag: ' (신규)', remove: '삭제',
    np_title: '새 장소 추가', place_name: '장소명', addr_opt: '주소 (선택)', cancel: '취소', add: '추가',
    saved_km: '저장했습니다. {0} km 주행.', saved_warn: '저장했습니다. 참고: ', locked: '이 달은 마감되었습니다. 수정할 수 없습니다.',
    locked_admin: ' "월 마감" 탭에서 다시 열 수 있습니다.', exists: '이 날짜는 {0}에 저장되었습니다. 수정 후 다시 저장할 수 있습니다.', fresh: '이 날짜는 아직 입력되지 않았습니다.',
    loading: '불러오는 중…', need_place_name: '장소명을 입력하세요.', no_car: '이 계정에 배정된 차량이 없습니다. 관리자(Hue)에게 문의하세요.',
    car: '차량', driver: '운전기사', weekday: ['일', '월', '화', '수', '목', '금', '토'],
    admin: '관리', tab_overview: '개요', tab_entry: '일일 입력', tab_places: '장소', tab_people: '운전기사 및 차량',
    tab_gas: '유가', tab_close: '월 마감', preview_btn: '운전기사 화면 보기', pv_label: '운전기사 화면으로 보는 중:',
    pv_note: '여기서 저장한 데이터는 실제 데이터입니다.', pv_exit: '← 관리 화면으로', month: '월',
    days_logged: '입력 일수', km: 'Km', other_short: '기타 (VND)', th_missing: '누락 일수', not_logged: '미입력',
    weekend: '주말', total: '합계', total_days: '일지 {0}일 / 해당 월 {1}일', missing: '일지가 없는 평일: ', none_missing: '누락된 날이 없습니다.',
    th_date: '날짜', th_time: '시간', th_route: '경로', th_start: '시작 km', th_end: '종료 km', th_fuel: '주유', th_other: '기타',
    th_note: '메모', th_by: '입력자', no_data: '아직 데이터가 없습니다.', edit_car: '+ 이 차량 입력/수정', open: '열림',
    closed: '마감', reopened: '다시 열림', st_pending: '승인 대기', st_active: '사용 중', st_inactive: '사용 안 함',
    pl_new: '+ 장소 추가', th_name: '이름', th_status: '상태', th_addr: '주소', th_uses: '사용 횟수', th_map: '지도',
    open_map: '열기', no_coord: '없음', edit: '수정', pe_edit: '장소 수정', pe_new: '장소 추가', pe_search: '위치 찾기',
    pe_search_ph: 'Google Maps 링크나 좌표를 붙여넣거나 주소를 입력하세요', find: '찾기',
    pe_help: '가장 빠른 방법: Google Maps 열기 → 장소 찾기 → 공유 → 링크 복사 → 위 칸에 붙여넣기 → 찾기.',
    pe_coord: '좌표 (자동 입력)', searching: '찾는 중…', not_found: '찾을 수 없습니다. Google Maps 링크를 붙여넣어 보세요.',
    drivers_link: '운전기사용 링크', drivers_link_how: '휴대폰에서 열기 → Google로 로그인 → 아래 표에 적힌 Gmail 선택. 한 번만 하면 되고 30일간 로그인이 유지됩니다.',
    drivers: '운전기사', cars: '차량', th_active: '사용 중', new_driver: '+ 새 운전기사', new_car: '+ 새 차량', th_plate: '번호판',
    th_model: '차종', th_year: '연식', th_odo: '현재 주행거리', th_driver: '운전기사', th_account: 'Portal 계정',
    no_account: '— 미연결 —', th_email: '운전기사 Google 이메일',
    email_hint: '운전기사가 Google 로그인에 쓰는 본인 Gmail(또는 @helinox.com) 주소. Portal 계정은 이 주소를 따릅니다.',
    acct_found: 'Portal 계정: {0} — 저장하면 운전기사에 연결됩니다.',
    acct_none: '아직 Portal 계정이 없습니다 — 첫 Google 로그인 때 생성되어(역할 Driver) 여기에 연결됩니다.',
    status_lbl: '상태', fp_clear: '필터 해제', rows_of: '{0} / {1}행',
    close_before: '운행 일지는 {1}부터 시작합니다. {0}은(는) 마감할 내용이 없습니다.',
    close_not_ended: '{0}이(가) 아직 끝나지 않았습니다 — 다음 달 1일부터 마감할 수 있습니다.',
    reopen_none: '{0}에 마감된 차량이 없습니다.',
    close_body1: 'Portal이 사용 중이거나 해당 월에 입력일이 있는 모든 차량의 {0} 일지 PDF를 만듭니다.',
    close_body2: 'PDF를 메일로 보낼 주소: {0}.',
    close_body3: '마감 후에는 운전기사가 이 달의 날짜를 수정할 수 없습니다. 수정하려면 월을 다시 여세요.',
    reopen_body1: '운전기사가 {0}의 날짜를 다시 수정할 수 있습니다.',
    reopen_body2: '이미 만든 PDF는 유지됩니다. 수정 후 새 PDF를 만들려면 월을 다시 마감하세요.',
    close_t: '{0} 마감', reopen_t: '{0} 다시 열기',
    acct_hint: '여기에 연결된 계정은 역할과 관계없이 운전기사 화면만 봅니다. 운행 일지 관리자는 Portal 전체를 그대로 씁니다.',
    acct_roles: '로그인 후 화면', not_signed_in: '아직 로그인한 적 없음', only_driver: '운전기사 화면만',
    keeps_portal: 'Portal 전체 (관리자)', gas_title: '월별 유가', gas_price: '가격 (VND/L)', ref_date: '기준일',
    gas_save: '유가 저장', close_title: '월 마감 / 다시 열기',
    gas_auto_title: '자동 유가', gas_grade: '연료', gas_zone: '지역 {0}', gas_current: '현재 적용 가격',
    gas_since: '{0}부터', gas_last_sync: '마지막 조회',
    gas_rule: 'Petrolimex의 일별 가격을 giaxanghomnay.com에서 3시간마다 가져옵니다. 월 가격 = 일별 가격의 평균이며, 직접 입력한 가격이 우선합니다.',
    gas_new: '+ 월 가격 입력', gas_title_one: '한 달의 유가', new_driver_t: '새 운전기사', edit_driver: '운전기사 수정',
    new_car_t: '새 차량', edit_car_t: '차량 수정',
    gas_sync: '지금 업데이트', gas_synced: '업데이트했습니다 ({0}일). 오늘 {1} VND/L.',
    gas_alert_to: '한 소스가 연속 2회(약 3시간) 실패하거나 두 소스의 가격이 다르면 Portal이 {0}에게 메일을 보내고, 24시간마다 다시 알리며, 정상화되면 알려 줍니다.',
    gas_failing_since: '{0}부터 실패 (연속 {1}회)', gas_mailed: '메일 발송됨',
    gas_src_giaxanghomnay: 'giaxanghomnay.com', gas_src_petrolimex: 'Petrolimex', gas_src_mismatch: '두 소스의 가격이 다름',
    gas_daily_title: '일별 가격 ({0})',
    gas_no_grade: '조회했지만 Petrolimex에 선택한 연료의 가격이 없습니다.', gas_auto_back: '{0}은(는) 다시 자동 가격을 사용합니다.',
    gas_type_hint: '한 달의 자동 가격을 바꿀 때만 직접 입력하세요.', gas_source: '출처',
    gas_src_auto: '자동 · {0}/{1}일 평균', gas_src_typed: '직접 입력', gas_use_auto: '자동 가격 사용',
    gas_log_title: '기록된 가격 변경 ({0})', gas_changed_on: '변경일',
    close_hint: '지난달은 1일 06:00에 자동으로 마감됩니다. 수정 후 다시 마감하려면 이 버튼을 사용하세요.',
    btn_close: '월 마감 (PDF 생성)', btn_reopen: '월 다시 열기', no_closings: '아직 마감한 달이 없습니다.',
    ask_close: '모든 차량의 {0}을(를) 마감할까요? PDF가 생성되어 메일로 발송됩니다.', ask_reopen: '{0}을(를) 다시 열까요? 운전기사가 다시 수정할 수 있습니다.',
    before_start: '운행 일지는 {0}부터 시작합니다. 이 달은 미입력 일수를 세지 않습니다.',
    closed_msg: '{0} 마감 완료', reopened_msg: '{0} 다시 열림', pdf: 'PDF', vnd_per_km: 'VND/km', gas_short: '유가',
    saved: '저장됨', active: '사용 중', yes: '예', no: '아니요',
    ot: '초과근무', ot_lunch: '점심시간 근무 (11:00–12:00)', ot_evening: '저녁 초과근무 (18:00 이후)', ot_other: '주말 초과근무',
    ot_morning: '아침 초과근무 (06:00 이전)', ot_weekend: '주말 초과근무 (하루 전체)', ot_morning_s: '아침',
    ot_rule: '근무 시간 06:00–18:00. 아침·저녁·주말 초과근무와 저녁 식대는 시작·종료 시간으로 계산됩니다.',
    ot_bad_times: '종료 시간이 시작 시간보다 앞입니다 — 시간을 확인하세요.', ot_lunch_hint: '점심시간에 근무했다면 몇 시부터 몇 시까지인지 선택하세요. 아니면 비워 두세요.',
    ot_kept: '이전 일지에서 가져온 날: 시간을 바꾸지 않는 한 기록된 초과근무가 유지됩니다.',
    other_costs: '기타 비용', cost_type: '비용 종류', cost_choose: '선택…', cost_new_opt: '+ 다른 종류 추가…',
    cost_new_ph: '새 비용 종류 이름', cost_amount: '금액 (VND)', cost_note: '메모 (선택)',
    add_cost: '+ 기타 비용 추가', need_cost_type: '각 기타 비용의 종류를 선택하세요.', untyped: '(종류 없음)', cost_line: '비용 {0}',
    ot_auto: '종료 시간으로 계산됨 — 다르면 수정하세요.', dinner: '저녁 식대', dinner_hint: '19:00 이후에 끝나면 자동으로 체크됩니다.',
    hours: '시', minutes: '분', remark: '오늘 메모', remark_ph: '예: 관리자 공장 이동, 공항 픽업…',
    typed_tag: '(입력한 그대로)', imported_from: '이 날짜는 Hue의 이전 일지에서 가져왔습니다: {0}.', th_ot: '초과근무', th_dinner: '저녁',
    ot_lunch_s: '점심', ot_evening_s: '저녁', ot_other_s: '주말', dinner_days: '{0}일',
    vnd_l: 'VND/L', email_ph: 'name@gmail.com',
  },
  zh: {
    tab_report: '报告', rp_title: '月度报告', rp_hint: '勾选要查看的月份（可跨年），或点快速选择。数据由每日日志计算，未结账的月份也能查看。',
    rp_quick: '快速选择', rp_last: '上月', rp_l3: '最近 3 个月', rp_l6: '最近 6 个月', rp_l12: '最近 12 个月',
    rp_h1: '{0} 上半年', rp_h2: '{0} 下半年', rp_year: '{0} 全年', rp_clear: '清除', rp_picked: '已选 {0} 个月',
    rp_all_cars: '全部车辆', rp_km: '行驶公里', rp_fuel: '油费', rp_vpk: '每公里油费', rp_gas: '越南油价', rp_gas_date: '油价日期',
    rp_month: '月份', rp_total: '合计', rp_pick_some: '请至少选择一个月查看报告。', rp_pdf: '下载 PDF', rp_nolog: '无日志',
    rp_typed: '手动输入的油价（来自 Hue 的 GW 报告或油价页）', rp_car: '{0} — {1} 的车', rp_chart: '每月每公里油费',
    rp_period: '期间：{0}', rp_mon: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    title: '司机日志', logout: '退出登录', today: '今天', notToday: '回到今天',
    time: '时间', start_time: '开始时间', end_time: '结束时间',
    start_hint: '通常为 6:00。只有行程需要提早出发时才选更早的时间。', lunch_calc: '午休加班：{0}',
    lunch_legacy: '此前记录：午休加班 {0}（无时间段）。', lunch_half: '午休：请同时选择开始和结束时间。',
    lunch_order: '午休：结束时间必须晚于开始时间。', clear_time: '清除',
    odo: '里程表 (km)', start_km: '起始 km', end_km: '结束 km', prev_km: '上次结束 km：{0}（{1}）',
    moved: '行驶：{0} km', route: '路线', leg: '第 {0} 段', from: '从', to: '到', add_trip: '+ 添加路段',
    costs: '费用', fuel: '油费 (VND)', other: '其他费用 (VND)', other_note: '其他费用备注',
    other_note_ph: '例：停车费、保养…', save: '保存', saving: '保存中…',
    choose: '— 请选择 —', new_place_opt: '+ 新地点…', new_tag: '（新）', remove: '删除',
    np_title: '添加新地点', place_name: '地点名称', addr_opt: '地址（可选）', cancel: '取消', add: '添加',
    saved_km: '已保存。行驶 {0} km。', saved_warn: '已保存。注意：', locked: '本月已结账，无法再修改。',
    locked_admin: ' 可在“月结”标签中重新打开。', exists: '此日期已于 {0} 保存。可修改后再次保存。', fresh: '此日期尚未录入。',
    loading: '加载中…', need_place_name: '请输入地点名称。', no_car: '此账户尚未分配车辆。请联系管理员（Hue）。',
    car: '车辆', driver: '司机', weekday: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
    admin: '管理', tab_overview: '概览', tab_entry: '每日录入', tab_places: '地点', tab_people: '司机和车辆',
    tab_gas: '油价', tab_close: '月结', preview_btn: '以司机身份查看', pv_label: '正在以司机身份查看：',
    pv_note: '在此保存的数据为真实数据。', pv_exit: '← 返回管理', month: '月份',
    days_logged: '已记录天数', km: 'Km', other_short: '其他 (VND)', th_missing: '缺失天数', not_logged: '未录入',
    weekend: '周末', total: '合计', total_days: '已记录 {0} 天 / 本月 {1} 天', missing: '未录入的工作日：', none_missing: '没有缺失的日期。',
    th_date: '日期', th_time: '时间', th_route: '路线', th_start: '起始 km', th_end: '结束 km', th_fuel: '油费', th_other: '其他',
    th_note: '备注', th_by: '录入人', no_data: '暂无数据。', edit_car: '+ 为此车录入/修改', open: '未结账',
    closed: '已结账', reopened: '已重新打开', st_pending: '待审批', st_active: '使用中', st_inactive: '停用',
    pl_new: '+ 添加地点', th_name: '名称', th_status: '状态', th_addr: '地址', th_uses: '使用次数', th_map: '地图',
    open_map: '打开', no_coord: '无', edit: '编辑', pe_edit: '编辑地点', pe_new: '添加地点', pe_search: '查找位置',
    pe_search_ph: '粘贴 Google Maps 链接、坐标，或输入地址', find: '查找',
    pe_help: '最快的方法：打开 Google Maps → 找到地点 → 分享 → 复制链接 → 粘贴到上方 → 查找。',
    pe_coord: '坐标（自动填写）', searching: '查找中…', not_found: '未找到。请尝试粘贴 Google Maps 链接。',
    drivers_link: '发给司机的链接', drivers_link_how: '在手机上打开 → 使用 Google 登录 → 选择下表中写明的 Gmail。只需一次，保持登录 30 天。',
    drivers: '司机', cars: '车辆', th_active: '使用中', new_driver: '+ 新司机', new_car: '+ 新车辆', th_plate: '车牌',
    th_model: '车型', th_year: '年份', th_odo: '当前里程', th_driver: '司机', th_account: 'Portal 账户',
    no_account: '— 未关联 —', th_email: '司机的 Google 邮箱',
    email_hint: '司机用于登录 Google 的个人 Gmail（或 @helinox.com）地址。Portal 账户跟随此地址。',
    acct_found: 'Portal 账户：{0} — 保存时关联到该司机。',
    acct_none: '尚无 Portal 账户 — 首次 Google 登录时自动创建（角色 Driver）并关联到此处。',
    status_lbl: '状态', fp_clear: '清除筛选', rows_of: '{0} / {1} 行',
    close_before: '司机日志从 {1} 开始；{0} 没有需要结账的内容。',
    close_not_ended: '{0} 尚未结束 — 下月 1 日起可结账。',
    reopen_none: '{0} 没有已结账的车辆。',
    close_body1: 'Portal 为每辆在用或本月有记录的车辆生成 {0} 日志 PDF。',
    close_body2: 'PDF 将通过邮件发送至：{0}。',
    close_body3: '结账后，司机无法修改本月的日期。如需更正，请重新打开该月。',
    reopen_body1: '司机可以再次修改 {0} 的日期。',
    reopen_body2: '已生成的 PDF 会保留。修改完成后，请再次结账以生成新的 PDF。',
    close_t: '结账 {0}', reopen_t: '重新打开 {0}',
    acct_hint: '在此关联的账户无论拥有什么角色，都只能看到司机界面。司机日志管理员保留整个 Portal。',
    acct_roles: '登录后可见', not_signed_in: '尚未登录', only_driver: '仅司机界面',
    keeps_portal: '整个 Portal（管理员）', gas_title: '每月油价', gas_price: '价格 (VND/升)', ref_date: '参考日期',
    gas_save: '保存油价', close_title: '月结 / 重新打开',
    gas_auto_title: '自动油价', gas_grade: '油品', gas_zone: '区域 {0}', gas_current: '现行价格',
    gas_since: '自 {0} 起', gas_last_sync: '最近读取',
    gas_rule: '每 3 小时从 giaxanghomnay.com 读取 Petrolimex 的每日价格。月价 = 各日平均值；手动输入的价格优先。',
    gas_new: '+ 输入月份价格', gas_title_one: '某月油价', new_driver_t: '新司机', edit_driver: '编辑司机',
    new_car_t: '新车辆', edit_car_t: '编辑车辆',
    gas_sync: '立即更新', gas_synced: '已更新（{0} 天）。今日 {1} VND/升。',
    gas_alert_to: '若某一来源连续失败 2 次（约 3 小时）或两个来源价格不一致，Portal 会发邮件给 {0}，每 24 小时再提醒一次，恢复后也会通知。',
    gas_failing_since: '自 {0} 起失败（连续 {1} 次）', gas_mailed: '已发邮件',
    gas_src_giaxanghomnay: 'giaxanghomnay.com', gas_src_petrolimex: 'Petrolimex', gas_src_mismatch: '两个来源价格不一致',
    gas_daily_title: '每日价格（{0}）',
    gas_no_grade: '已读取，但 Petrolimex 没有所选油品的价格。', gas_auto_back: '{0} 重新使用自动价格。',
    gas_type_hint: '仅在需要替换某月的自动价格时手动输入。', gas_source: '来源',
    gas_src_auto: '自动 · {0}/{1} 天平均', gas_src_typed: '手动输入', gas_use_auto: '使用自动价格',
    gas_log_title: '已记录的价格变动（{0}）', gas_changed_on: '变动日期',
    close_hint: '上月于 1 日 06:00 自动结账。修改后可用这些按钮重新结账。',
    btn_close: '月结（生成 PDF）', btn_reopen: '重新打开该月', no_closings: '尚无已结账的月份。',
    ask_close: '为所有车辆结账 {0}？将生成 PDF 并发送邮件。', ask_reopen: '重新打开 {0}？司机可再次修改。',
    before_start: '司机日志从 {0} 开始；本月不计算未录入的日期。',
    closed_msg: '已结账 {0}', reopened_msg: '已重新打开 {0}', pdf: 'PDF', vnd_per_km: 'VND/km', gas_short: '油价',
    saved: '已保存', active: '使用中', yes: '是', no: '否',
    ot: '加班', ot_lunch: '午休时间工作（11:00–12:00）', ot_evening: '晚间加班（18:00 后）', ot_other: '周末加班',
    ot_morning: '早间加班（06:00 前）', ot_weekend: '周末加班（全天）', ot_morning_s: '早',
    ot_rule: '工作时间 06:00–18:00。早间、晚间、周末加班及晚餐补贴按开始和结束时间自动计算。',
    ot_bad_times: '结束时间早于开始时间 — 请检查时间。', ot_lunch_hint: '如果午休时间有工作，请选择起止时间；没有则留空。',
    ot_kept: '从旧日志导入：除非修改时间，否则保留已记录的加班。',
    other_costs: '其他费用', cost_type: '费用类型', cost_choose: '请选择…', cost_new_opt: '+ 添加其他类型…',
    cost_new_ph: '新费用类型名称', cost_amount: '金额 (VND)', cost_note: '备注（可选）',
    add_cost: '+ 添加其他费用', need_cost_type: '请为每项其他费用选择类型。', untyped: '（无类型）', cost_line: '费用 {0}',
    ot_auto: '根据结束时间计算 — 如有不同请修改。', dinner: '晚餐补贴', dinner_hint: '当天 19:00 后结束时自动勾选。',
    hours: '时', minutes: '分', remark: '当日备注', remark_ph: '例：送领导去工厂、机场接人…',
    typed_tag: '（按输入原样）', imported_from: '此日期从 Hue 的旧日志导入：{0}。', th_ot: '加班', th_dinner: '晚餐',
    ot_lunch_s: '午', ot_evening_s: '晚', ot_other_s: '周末', dinner_days: '{0} 天',
    vnd_l: 'VND/升', email_ph: 'name@gmail.com',
  },
  // fr (task #1173, 04/11/2026)
  fr: {
    tab_report: "Rapport",
    rp_title: "Rapport mensuel",
    rp_hint: "Cochez les mois souhaités (y compris sur plusieurs années) ou utilisez une sélection rapide. Les chiffres sont calculés à partir des journaux quotidiens : un mois en cours s'affiche tel qu'il est à ce jour.",
    rp_quick: "Sélection rapide",
    rp_last: "Mois dernier",
    rp_l3: "3 derniers mois",
    rp_l6: "6 derniers mois",
    rp_l12: "12 derniers mois",
    rp_h1: "1er semestre {0}",
    rp_h2: "2e semestre {0}",
    rp_year: "Année {0}",
    rp_clear: "Effacer",
    rp_picked: "{0} mois sélectionné(s)",
    rp_all_cars: "Tous les véhicules",
    rp_km: "Km parcourus",
    rp_fuel: "Carburant payé",
    rp_vpk: "Carburant par km",
    rp_gas: "Prix de l'essence au Vietnam",
    rp_gas_date: "Date du prix",
    rp_month: "Mois",
    rp_total: "TOTAL",
    rp_pick_some: "Sélectionnez au moins un mois pour afficher le rapport.",
    rp_pdf: "Télécharger le PDF",
    rp_nolog: "aucun journal",
    rp_typed: "Prix saisi (rapport GW de Hue ou onglet Prix de l'essence)",
    rp_car: "{0} — véhicule de {1}",
    rp_chart: "Carburant par km, par mois",
    rp_period: "Période : {0}",
    rp_mon: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    title: "Journal du chauffeur",
    logout: "Se déconnecter",
    today: "Aujourd'hui",
    notToday: "Retour à aujourd'hui",
    time: "Heure",
    start_time: "Heure de début",
    end_time: "Heure de fin",
    start_hint: "Généralement 6:00. Choisissez plus tôt uniquement si un trajet exige un départ matinal.",
    lunch_calc: "Heures sup. du déjeuner : {0}",
    lunch_legacy: "Enregistré précédemment : {0} d'heures sup. du déjeuner (sans plage horaire).",
    lunch_half: "Pause déjeuner : indiquez le début et la fin.",
    lunch_order: "Pause déjeuner : la fin doit être après le début.",
    clear_time: "Effacer",
    odo: "Compteur (km)",
    start_km: "Km de départ",
    end_km: "Km d'arrivée",
    prev_km: "Dernier km d'arrivée : {0} ({1})",
    moved: "Parcouru : {0} km",
    route: "Itinéraire",
    leg: "Trajet {0}",
    from: "De",
    to: "À",
    add_trip: "+ Ajouter un trajet",
    costs: "Frais",
    fuel: "Carburant (VND)",
    other: "Autres frais (VND)",
    other_note: "Note sur les autres frais",
    other_note_ph: "ex. parking, entretien...",
    save: "Enregistrer",
    saving: "Enregistrement…",
    choose: "— choisir —",
    new_place_opt: "+ Nouveau lieu…",
    new_tag: " (nouveau)",
    remove: "Supprimer",
    np_title: "Ajouter un lieu",
    place_name: "Nom du lieu",
    addr_opt: "Adresse (facultatif)",
    cancel: "Annuler",
    add: "Ajouter",
    saved_km: "Enregistré. {0} km parcourus.",
    saved_warn: "Enregistré. Attention : ",
    locked: "Ce mois est clôturé. La modification est verrouillée.",
    locked_admin: " Rouvrez-le dans « Clôture du mois ».",
    exists: "Cette journée a été enregistrée à {0}. Vous pouvez la modifier et l'enregistrer à nouveau.",
    fresh: "Rien n'a encore été saisi pour cette journée.",
    loading: "Chargement…",
    need_place_name: "Saisissez un nom de lieu.",
    no_car: "Aucun véhicule n'est attribué à ce compte. Contactez l'administrateur (Hue).",
    car: "Véhicule",
    driver: "Chauffeur",
    weekday: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
    admin: "Admin",
    tab_overview: "Vue d'ensemble",
    tab_entry: "Saisie du jour",
    tab_places: "Lieux",
    tab_people: "Chauffeurs et véhicules",
    tab_gas: "Prix de l'essence",
    tab_close: "Clôture du mois",
    preview_btn: "Vue chauffeur",
    pv_label: "Affichage en tant que chauffeur :",
    pv_note: "Les données enregistrées ici sont réelles.",
    pv_exit: "← Retour à l'admin",
    month: "Mois",
    days_logged: "Jours saisis",
    km: "Km",
    other_short: "Autres (VND)",
    th_missing: "Jours manquants",
    not_logged: "Non saisi",
    weekend: "Week-end",
    total: "Total",
    total_days: "{0} jours saisis / {1} jours dans le mois",
    missing: "Jours ouvrés sans journal : ",
    none_missing: "Aucun jour manquant.",
    th_date: "Date",
    th_time: "Heure",
    th_route: "Itinéraire",
    th_start: "Km départ",
    th_end: "Km arrivée",
    th_fuel: "Carburant",
    th_other: "Autres",
    th_note: "Note",
    th_by: "Saisi par",
    no_data: "Aucune donnée pour le moment.",
    edit_car: "+ Saisir/modifier pour ce véhicule",
    open: "Ouvert",
    closed: "Clôturé",
    reopened: "Rouvert",
    st_pending: "En attente",
    st_active: "Actif",
    st_inactive: "Inactif",
    pl_new: "+ Ajouter un lieu",
    th_name: "Nom",
    th_status: "Statut",
    th_addr: "Adresse",
    th_uses: "Utilisations",
    th_map: "Carte",
    ot: "Heures sup.",
    ot_lunch: "Travail pendant la pause déjeuner (11:00–12:00)",
    ot_evening: "Heures sup. du soir (après 18:00)",
    ot_other: "Heures sup. de week-end",
    ot_morning: "Heures sup. du matin (avant 06:00)",
    ot_weekend: "Heures sup. de week-end (journée entière)",
    ot_morning_s: "Matin",
    ot_rule: "Horaires de travail 06:00–18:00. Les heures sup. du matin, du soir et du week-end ainsi que l'indemnité de dîner sont déduites des heures de début et de fin.",
    ot_bad_times: "L'heure de fin est avant l'heure de début — vérifiez les heures.",
    ot_lunch_hint: "Si vous avez travaillé pendant la pause déjeuner, indiquez de quelle heure à quelle heure. Sinon, laissez vide.",
    ot_kept: "Importé de l'ancien journal : ses heures sup. enregistrées sont conservées tant que les heures ne changent pas.",
    other_costs: "Autres frais",
    cost_type: "Type de frais",
    cost_choose: "Choisir…",
    cost_new_opt: "+ Ajouter un autre type…",
    cost_new_ph: "Nom du nouveau type",
    cost_amount: "Montant (VND)",
    cost_note: "Note (facultatif)",
    add_cost: "+ Ajouter un autre frais",
    need_cost_type: "Choisissez le type de chaque autre frais.",
    untyped: "(sans type)",
    cost_line: "Frais {0}",
    ot_auto: "Calculé d'après l'heure de fin — modifiez-le si nécessaire.",
    dinner: "Indemnité de dîner",
    dinner_hint: "Cochée automatiquement lorsque la journée se termine après 19:00.",
    hours: "h",
    minutes: "min",
    remark: "Note du jour",
    remark_ph: "ex. a conduit les managers à l'usine, accueil à l'aéroport…",
    typed_tag: "(saisi)",
    imported_from: "Cette journée a été importée de l'ancien journal de Hue : {0}.",
    th_ot: "Heures sup.",
    th_dinner: "Dîner",
    ot_lunch_s: "Déj.",
    ot_evening_s: "Soir",
    ot_other_s: "Week-end",
    dinner_days: "{0} jours",
    open_map: "Ouvrir",
    no_coord: "Aucune",
    edit: "Modifier",
    pe_edit: "Modifier le lieu",
    pe_new: "Ajouter un lieu",
    pe_search: "Trouver l'emplacement",
    pe_search_ph: "Collez un lien Google Maps, des coordonnées, ou saisissez une adresse",
    find: "Rechercher",
    pe_help: "Le plus rapide : ouvrez Google Maps → trouvez le lieu → Partager → Copier le lien → collez-le ci-dessus → Rechercher.",
    pe_coord: "Coordonnées (remplies automatiquement)",
    searching: "Recherche…",
    not_found: "Introuvable. Essayez de coller un lien Google Maps.",
    drivers_link: "Lien pour les chauffeurs",
    drivers_link_how: "ouvrez-le sur le téléphone → Se connecter avec Google → choisissez le Gmail indiqué ci-dessous. Une seule fois ; la session reste active 30 jours.",
    drivers: "Chauffeurs",
    cars: "Véhicules",
    th_active: "Actif",
    new_driver: "+ Nouveau chauffeur",
    new_car: "+ Nouveau véhicule",
    th_plate: "Plaque",
    th_model: "Modèle",
    th_year: "Année",
    th_odo: "Compteur",
    th_driver: "Chauffeur",
    th_account: "Compte Portal",
    no_account: "— non lié —",
    th_email: "E-mail Google du chauffeur",
    email_hint: "L'adresse Gmail (ou @helinox.com) personnelle du chauffeur, avec laquelle il se connecte à Google. Le compte Portal suit cette adresse.",
    acct_found: "Compte Portal : {0} — lié au chauffeur à l'enregistrement.",
    acct_none: "Pas encore de compte Portal — la première connexion Google le crée (rôle Driver) et le lie ici.",
    status_lbl: "Statut",
    fp_clear: "Effacer les filtres",
    rows_of: "{0} lignes sur {1}",
    close_before: "Le journal du chauffeur commence en {1} ; il n'y a rien à clôturer pour {0}.",
    close_not_ended: "{0} n'est pas terminé — il peut être clôturé à partir du 1er du mois suivant.",
    reopen_none: "Aucun véhicule n'est clôturé pour {0}.",
    close_body1: "Le Portal génère le PDF du journal de {0} pour chaque véhicule utilisé ou ayant une journée dans le mois.",
    close_body2: "Les PDF sont envoyés par e-mail à : {0}.",
    close_body3: "Une fois clôturé, les chauffeurs ne peuvent plus modifier de journée de ce mois. Pour corriger, rouvrez le mois.",
    reopen_body1: "Les chauffeurs pourront à nouveau modifier les journées de {0}.",
    reopen_body2: "Les PDF déjà générés sont conservés. Après les modifications, clôturez à nouveau le mois pour obtenir de nouveaux PDF.",
    close_t: "Clôturer {0}",
    reopen_t: "Rouvrir {0}",
    acct_hint: "Un compte lié ici ne voit que l'écran chauffeur, quels que soient ses rôles. Un administrateur du journal du chauffeur conserve tout le portail.",
    acct_roles: "Après connexion, voit",
    not_signed_in: "Pas encore connecté",
    only_driver: "Écran chauffeur uniquement",
    keeps_portal: "Tout le portail (admin)",
    gas_title: "Prix mensuel de l'essence",
    gas_price: "Prix (VND/L)",
    ref_date: "Date de référence",
    gas_save: "Enregistrer le prix de l'essence",
    close_title: "Clôturer / rouvrir le mois",
    gas_auto_title: "Prix automatique de l'essence",
    gas_grade: "Carburant",
    gas_zone: "Zone {0}",
    gas_current: "Prix en vigueur",
    gas_since: "depuis le {0}",
    gas_last_sync: "Dernière lecture",
    gas_rule: "Le prix de Petrolimex pour chaque jour, lu sur giaxanghomnay.com toutes les 3 heures. Un mois = la moyenne de ses jours ; un prix saisi est prioritaire.",
    gas_new: "+ Saisir un mois",
    gas_title_one: "Prix de l'essence d'un mois",
    new_driver_t: "Nouveau chauffeur",
    edit_driver: "Modifier le chauffeur",
    new_car_t: "Nouveau véhicule",
    edit_car_t: "Modifier le véhicule",
    gas_sync: "Mettre à jour",
    gas_synced: "Mis à jour ({0} jour(s)). Aujourd'hui {1} VND/L.",
    gas_alert_to: "Si une source échoue deux fois de suite (environ 3 heures) ou si les deux divergent, le Portal envoie un e-mail à {0}, puis toutes les 24 heures, et une dernière fois lorsque tout refonctionne.",
    gas_failing_since: "en échec depuis {0} ({1} exécutions consécutives)",
    gas_mailed: "e-mail envoyé",
    gas_src_giaxanghomnay: "giaxanghomnay.com",
    gas_src_petrolimex: "Petrolimex",
    gas_src_mismatch: "Les deux sources divergent",
    gas_daily_title: "Prix par jour ({0})",
    gas_no_grade: "Lu, mais Petrolimex n'indique aucun prix pour le carburant choisi.",
    gas_auto_back: "{0} utilise de nouveau le prix automatique.",
    gas_type_hint: "Saisissez un prix uniquement pour remplacer le prix automatique d'un mois.",
    gas_source: "Source",
    gas_src_auto: "Automatique · moyenne de {0}/{1} jours",
    gas_src_typed: "Saisi",
    gas_use_auto: "Utiliser le prix automatique",
    gas_log_title: "Changements de prix enregistrés ({0})",
    gas_changed_on: "Modifié le",
    close_hint: "Le mois précédent est clôturé automatiquement à 06:00 le 1er. Utilisez ces boutons pour clôturer à nouveau après des modifications.",
    btn_close: "Clôturer le mois (créer le PDF)",
    btn_reopen: "Rouvrir le mois",
    no_closings: "Aucun mois clôturé pour le moment.",
    ask_close: "Clôturer {0} pour tous les véhicules ? Les PDF seront créés et envoyés par e-mail.",
    ask_reopen: "Rouvrir {0} ? Les chauffeurs pourront à nouveau modifier.",
    before_start: "Le journal du chauffeur commence en {0} ; aucun jour de ce mois n'est considéré comme manquant.",
    closed_msg: "{0} clôturé",
    reopened_msg: "{0} rouvert",
    pdf: "PDF",
    vnd_per_km: "VND/km",
    gas_short: "Prix de l'essence",
    saved: "Enregistré",
    active: "Actif",
    yes: "Oui",
    no: "Non",
    vnd_l: "VND/L",
    email_ph: "nom@gmail.com",
  },
  // ja (task #1173, 04/11/2026)
  ja: {
    tab_report: "レポート",
    rp_title: "月次レポート",
    rp_hint: "対象の月にチェックを入れてください(年をまたいでも選べます)。クイック選択も使えます。数値は日々のログから集計するため、未締めの月は現時点の内容が表示されます。",
    rp_quick: "クイック選択",
    rp_last: "先月",
    rp_l3: "直近3か月",
    rp_l6: "直近6か月",
    rp_l12: "直近12か月",
    rp_h1: "{0}年 上半期",
    rp_h2: "{0}年 下半期",
    rp_year: "{0}年",
    rp_clear: "クリア",
    rp_picked: "{0}か月を選択中",
    rp_all_cars: "全車両",
    rp_km: "走行距離",
    rp_fuel: "燃料費",
    rp_vpk: "1kmあたり燃料費",
    rp_gas: "ベトナムのガソリン価格",
    rp_gas_date: "価格の日付",
    rp_month: "月",
    rp_total: "合計",
    rp_pick_some: "レポートを表示するには、1か月以上選択してください。",
    rp_pdf: "PDFをダウンロード",
    rp_nolog: "ログなし",
    rp_typed: "手入力の価格(HueのGWレポートまたはガソリン価格タブより)",
    rp_car: "{0} — {1}さんの車両",
    rp_chart: "月別の1kmあたり燃料費",
    rp_period: "期間:{0}",
    rp_mon: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    title: "ドライバーログ",
    logout: "ログアウト",
    today: "今日",
    notToday: "今日に戻る",
    time: "時間",
    start_time: "開始時刻",
    end_time: "終了時刻",
    start_hint: "通常は6:00です。早朝出発が必要な場合のみ、早い時刻を選んでください。",
    lunch_calc: "昼休み残業:{0}",
    lunch_legacy: "以前の記録:昼休み残業 {0}(時間帯の記録なし)。",
    lunch_half: "昼休み:開始と終了の両方を選択してください。",
    lunch_order: "昼休み:終了は開始より後にしてください。",
    clear_time: "クリア",
    odo: "オドメーター(km)",
    start_km: "開始km",
    end_km: "終了km",
    prev_km: "前回の終了km:{0}({1})",
    moved: "走行距離:{0} km",
    route: "ルート",
    leg: "区間 {0}",
    from: "出発地",
    to: "到着地",
    add_trip: "+ 区間を追加",
    costs: "費用",
    fuel: "燃料費(VND)",
    other: "その他の費用(VND)",
    other_note: "その他の費用のメモ",
    other_note_ph: "例:駐車場、整備など...",
    save: "保存",
    saving: "保存中…",
    choose: "— 選択 —",
    new_place_opt: "+ 新しい場所…",
    new_tag: "(新規)",
    remove: "削除",
    np_title: "場所を追加",
    place_name: "場所の名前",
    addr_opt: "住所(任意)",
    cancel: "キャンセル",
    add: "追加",
    saved_km: "保存しました。走行距離は {0} km です。",
    saved_warn: "保存しました。ご注意:",
    locked: "この月は締め済みです。編集はできません。",
    locked_admin: "「月次締め」で再開してください。",
    exists: "この日は {0} に保存済みです。編集して再度保存できます。",
    fresh: "この日の記録はまだありません。",
    loading: "読み込み中…",
    need_place_name: "場所の名前を入力してください。",
    no_car: "このアカウントには車両が割り当てられていません。管理者(Hue)に連絡してください。",
    car: "車両",
    driver: "ドライバー",
    weekday: ["日", "月", "火", "水", "木", "金", "土"],
    admin: "管理者",
    tab_overview: "概要",
    tab_entry: "日次入力",
    tab_places: "場所",
    tab_people: "ドライバーと車両",
    tab_gas: "ガソリン価格",
    tab_close: "月次締め",
    preview_btn: "ドライバー画面",
    pv_label: "ドライバーとして表示中:",
    pv_note: "ここで保存したデータは実際のデータです。",
    pv_exit: "← 管理画面に戻る",
    month: "月",
    days_logged: "記録日数",
    km: "km",
    other_short: "その他(VND)",
    th_missing: "未記録日数",
    not_logged: "未記録",
    weekend: "週末",
    total: "合計",
    total_days: "{0}日記録済み / 当月 {1}日",
    missing: "ログのない平日:",
    none_missing: "未記録の日はありません。",
    th_date: "日付",
    th_time: "時間",
    th_route: "ルート",
    th_start: "開始km",
    th_end: "終了km",
    th_fuel: "燃料費",
    th_other: "その他",
    th_note: "メモ",
    th_by: "入力者",
    no_data: "データはまだありません。",
    edit_car: "+ この車両の入力/編集",
    open: "未締め",
    closed: "締め済み",
    reopened: "再開済み",
    st_pending: "保留中",
    st_active: "有効",
    st_inactive: "無効",
    pl_new: "+ 場所を追加",
    th_name: "名前",
    th_status: "ステータス",
    th_addr: "住所",
    th_uses: "利用回数",
    th_map: "地図",
    ot: "残業",
    ot_lunch: "昼休みも勤務(11:00–12:00)",
    ot_evening: "夕方の残業(18:00以降)",
    ot_other: "週末の残業",
    ot_morning: "早朝の残業(06:00前)",
    ot_weekend: "週末の残業(終日)",
    ot_morning_s: "朝",
    ot_rule: "勤務時間は06:00–18:00です。早朝・夕方・週末の残業と夕食手当は、開始・終了時刻から算出されます。",
    ot_bad_times: "終了時刻が開始時刻より前です。時刻を確認してください。",
    ot_lunch_hint: "昼休みも勤務した場合は、何時から何時までかを選んでください。該当しない場合は空欄のままにしてください。",
    ot_kept: "旧ログから取り込んだデータです。時刻を変更しない限り、記録済みの残業はそのまま保持されます。",
    other_costs: "その他の費用",
    cost_type: "費用の種類",
    cost_choose: "選択…",
    cost_new_opt: "+ 別の種類を追加…",
    cost_new_ph: "新しい種類の名前",
    cost_amount: "金額(VND)",
    cost_note: "メモ(任意)",
    add_cost: "+ 費用を追加",
    need_cost_type: "その他の費用ごとに種類を選択してください。",
    untyped: "(種類なし)",
    cost_line: "費用 {0}",
    ot_auto: "終了時刻から算出しました。異なる場合は変更してください。",
    dinner: "夕食手当",
    dinner_hint: "19:00以降に終了する日は自動でチェックされます。",
    hours: "時間",
    minutes: "分",
    remark: "今日のメモ",
    remark_ph: "例:管理職を工場へ送迎、空港への出迎え…",
    typed_tag: "(手入力)",
    imported_from: "この日はHueの旧ログから取り込みました:{0}。",
    th_ot: "残業",
    th_dinner: "夕食",
    ot_lunch_s: "昼",
    ot_evening_s: "夕",
    ot_other_s: "週末",
    dinner_days: "{0}日",
    open_map: "開く",
    no_coord: "なし",
    edit: "編集",
    pe_edit: "場所を編集",
    pe_new: "場所を追加",
    pe_search: "位置を検索",
    pe_search_ph: "Google Mapsのリンク、座標を貼り付けるか、住所を入力してください",
    find: "検索",
    pe_help: "最も簡単な方法:Google Mapsを開く → 場所を探す → 共有 → リンクをコピー → 上に貼り付け → 検索。",
    pe_coord: "座標(自動入力)",
    searching: "検索中…",
    not_found: "見つかりません。Google Mapsのリンクを貼り付けてみてください。",
    drivers_link: "ドライバー用リンク",
    drivers_link_how: "スマートフォンで開く → Googleでログイン → 下に記載のGmailを選択。最初の1回のみで、30日間ログイン状態が続きます。",
    drivers: "ドライバー",
    cars: "車両",
    th_active: "有効",
    new_driver: "+ ドライバーを追加",
    new_car: "+ 車両を追加",
    th_plate: "ナンバー",
    th_model: "車種",
    th_year: "年式",
    th_odo: "オドメーター",
    th_driver: "ドライバー",
    th_account: "Portalアカウント",
    no_account: "— 未連携 —",
    th_email: "ドライバーのGoogleメール",
    email_hint: "ドライバー本人がGoogleへのログインに使うGmail(または@helinox.com)のアドレスです。Portalアカウントはこのアドレスに連動します。",
    acct_found: "Portalアカウント:{0} — 保存するとドライバーに連携されます。",
    acct_none: "Portalアカウントはまだありません。最初のGoogleログイン時に作成され(ロールはDriver)、ここに連携されます。",
    status_lbl: "ステータス",
    fp_clear: "フィルターをクリア",
    rows_of: "{1}行中 {0}行",
    close_before: "ドライバーログは{1}から開始のため、{0}には締める対象がありません。",
    close_not_ended: "{0}はまだ終了していません。翌月1日から締めることができます。",
    reopen_none: "{0}に締め済みの車両はありません。",
    close_body1: "Portalは、使用中の車両、または当月に記録がある車両すべての{0}ログPDFを作成します。",
    close_body2: "PDFの送信先:{0}。",
    close_body3: "締めると、ドライバーはこの月の記録を変更できなくなります。修正する場合は、月を再開してください。",
    reopen_body1: "ドライバーが{0}の記録を再び変更できるようになります。",
    reopen_body2: "作成済みのPDFは保持されます。編集後に再度月を締めると、新しいPDFが作成されます。",
    close_t: "{0}を締める",
    reopen_t: "{0}を再開する",
    acct_hint: "ここで連携したアカウントは、保有するロールにかかわらずドライバー画面のみ表示されます。ドライバーログの管理者はポータル全体を利用できます。",
    acct_roles: "ログイン後の表示",
    not_signed_in: "未ログイン",
    only_driver: "ドライバー画面のみ",
    keeps_portal: "ポータル全体(管理者)",
    gas_title: "月別ガソリン価格",
    gas_price: "価格(VND/L)",
    ref_date: "基準日",
    gas_save: "ガソリン価格を保存",
    close_title: "月の締め/再開",
    gas_auto_title: "ガソリン価格の自動取得",
    gas_grade: "燃料",
    gas_zone: "地域 {0}",
    gas_current: "現在の価格",
    gas_since: "{0}から",
    gas_last_sync: "最終取得",
    gas_rule: "Petrolimexの日別価格を、giaxanghomnay.comから3時間ごとに取得します。月の価格は日々の平均で、手入力した価格が優先されます。",
    gas_new: "+ 月を入力",
    gas_title_one: "月のガソリン価格",
    new_driver_t: "ドライバーを追加",
    edit_driver: "ドライバーを編集",
    new_car_t: "車両を追加",
    edit_car_t: "車両を編集",
    gas_sync: "今すぐ更新",
    gas_synced: "更新しました({0}日分)。本日は {1} VND/L です。",
    gas_alert_to: "取得元が2回連続(約3時間)で失敗した場合、または2つの取得元の値が一致しない場合、Portalは{0}にメールを送信します。以降は24時間ごとに送信し、復旧時にも送信します。",
    gas_failing_since: "{0}から失敗中(連続{1}回)",
    gas_mailed: "メール送信済み",
    gas_src_giaxanghomnay: "giaxanghomnay.com",
    gas_src_petrolimex: "Petrolimex",
    gas_src_mismatch: "2つの取得元の値が一致しません",
    gas_daily_title: "日別価格({0})",
    gas_no_grade: "取得しましたが、選択した燃料のPetrolimex価格がありません。",
    gas_auto_back: "{0}は自動取得の価格に戻りました。",
    gas_type_hint: "月の自動価格を置き換える場合のみ、価格を入力してください。",
    gas_source: "取得元",
    gas_src_auto: "自動 · {0}/{1}日の平均",
    gas_src_typed: "手入力",
    gas_use_auto: "自動価格を使用",
    gas_log_title: "記録された価格変更({0})",
    gas_changed_on: "変更日",
    close_hint: "先月分は毎月1日の06:00に自動で締められます。編集後に再度締める場合は、これらのボタンを使ってください。",
    btn_close: "月を締める(PDFを作成)",
    btn_reopen: "月を再開",
    no_closings: "締め済みの月はまだありません。",
    ask_close: "{0}を全車両分締めますか?PDFが作成され、メールで送信されます。",
    ask_reopen: "{0}を再開しますか?ドライバーが再び編集できるようになります。",
    before_start: "ドライバーログは{0}から開始のため、この月に未記録の日はありません。",
    closed_msg: "{0}を締めました",
    reopened_msg: "{0}を再開しました",
    pdf: "PDF",
    vnd_per_km: "VND/km",
    gas_short: "ガソリン価格",
    saved: "保存しました",
    active: "有効",
    yes: "はい",
    no: "いいえ",
    vnd_l: "VND/L",
    email_ph: "name@gmail.com",
  },
};
const fill = (s, ...a) => String(s).replace(/\{(\d)\}/g, (_, i) => (a[Number(i)] ?? ''));
/* THE PORTAL'S LANGUAGE, AND VIETNAMESE FOR THE DRIVERS (Max, 2026-10-21: "phần driver thì ngôn ngữ theo mặc định
   hệ thống chỉ riêng account các anh thì là tiếng việt mặc định"). The admin screens follow the language button in the
   portal's header — English, Korean, Vietnamese or Simplified Chinese (ko and zh since the globe of 2026-10-21; before
   that a Korean reader got English). A driver's own screen is always Vietnamese, and so is the form inside an admin's
   "View as driver", which is there to show exactly what the driver sees (its bar of admin controls keeps the admin's
   language). Still no switch on the log itself, and a 'dlLang' an earlier visit stored is still not read (Max, earlier
   the same day: "bỏ cái chỗ EN nhé"). A key missing from a table reads English. */
const langOf = (code) => {
  const c = String(code || '').toLowerCase();
  if (c.startsWith('vi')) return 'vi';
  if (c.startsWith('ko')) return 'ko';
  if (c.startsWith('zh')) return 'zh';
  if (c.startsWith('fr')) return 'fr';
  if (c.startsWith('ja')) return 'ja';
  return 'en';
};
const useLang = (forceVi) => {
  const { i18n } = useTranslation();
  const lang = forceVi ? 'vi' : langOf(i18n.language);
  const t = useCallback((k, ...a) => fill((T[lang] || T.en)[k] ?? T.en[k] ?? k, ...a), [lang]);
  return useMemo(() => ({ lang, t, w: (T[lang] || T.en).weekday || T.en.weekday }), [lang, t]);
};
const n0 = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-US'));
const addDays = (d, k) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + k); return x.toISOString().slice(0, 10); };
const dmy = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');
const wdOf = (d) => new Date(`${d}T00:00:00Z`).getUTCDay();

/* A number box that shows its thousands (a VND figure is read at a glance, not counted). Keeps the plain digits. */
const NumBox = ({ value, onChange, ...rest }) => (
  <input type="text" inputMode="numeric" autoComplete="off" {...rest}
    value={value === null || value === undefined || value === '' ? '' : Number(String(value).replace(/\D/g, '') || 0).toLocaleString('en-US')}
    onChange={(e) => { const raw = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, ''); onChange(raw); }} />
);

/* A TIME as two selects, hour : minute (Max, 2026-10-21, with a driver's iPhone where the time box ran past its card
   and read "2:13 CH"). The phone draws its own time box — its width, a 12-hour clock in the phone's language, a "Đặt lại"
   button — and none of it can be styled; two selects look the same on every phone, read 24-hour, and are two taps.
   5-minute steps: of the 1,837 days in the old logs, 1,836 start on a multiple of 5. An odd stored value stays offered.
   "HH:MM" in and out, '' for no time; the minute waits until there is an hour. */
const pad2 = (n) => String(n).padStart(2, '0');
const ALL_HOURS = [...Array(24).keys()];
const TimeBox = ({ value, onChange, hours = ALL_HOURS, testid, t }) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(value || '');
  const h = m ? Number(m[1]) : '';
  const mi = m ? Number(m[2]) : '';
  const hs = h === '' || hours.includes(h) ? hours : [...hours, h].sort((a, b) => a - b);
  const ms = [...Array(12).keys()].map((k) => k * 5);
  if (mi !== '' && !ms.includes(mi)) { ms.push(mi); ms.sort((a, b) => a - b); }
  return (
    <div className="dl-time" data-testid={testid}>
      <select value={h} aria-label={t('hours')} data-testid={`${testid}-h`}
        onChange={(e) => onChange(e.target.value === '' ? '' : `${pad2(e.target.value)}:${pad2(mi === '' ? 0 : mi)}`)}>
        <option value="">--</option>
        {hs.map((k) => <option key={k} value={k}>{pad2(k)}</option>)}
      </select>
      <b aria-hidden="true">:</b>
      <select value={mi} disabled={h === ''} aria-label={t('minutes')} data-testid={`${testid}-m`}
        onChange={(e) => onChange(`${pad2(h)}:${pad2(e.target.value)}`)}>
        {h === '' && <option value="">--</option>}
        {ms.map((k) => <option key={k} value={k}>{pad2(k)}</option>)}
      </select>
    </div>
  );
};
/* LUNCH OVERTIME FROM A TIME RANGE (Max, 2026-10-21: "giờ trưa thì cần nhập khung giờ nhé chứ ko chọn số giờ và phút").
   The break is 11:00-12:00; what of the range falls inside it is the overtime — the server works it out the same way
   (lunchFromRange in driverLog.js). null = no range, { bad } = the end is not after the start. */
const LUNCH_HOURS = [11, 12];
const toMin = (x) => { const m = /^(\d{1,2}):(\d{2})/.exec(x || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const lunchFromRange = (from, to) => {
  const a = toMin(from);
  const b = toMin(to);
  if (a === null && b === null) return null;
  if (a === null || b === null) return { half: true };
  if (b <= a) return { bad: true };
  return { min: Math.max(0, Math.min(b, 720) - Math.max(a, 660)) };
};
const hm = (m) => (m ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}` : '');
/* OVERTIME FROM THE TIMES (Max, 2026-10-21): the working day is 06:00-18:00; before it is morning overtime, after it
   evening overtime; a Saturday or Sunday's whole time is weekend overtime; ending after 19:00 earns the dinner allowance.
   The SERVER works it out on every save (otFromTimes in driverLog.js) — this is the same rule, drawn while the driver
   types, so the card shows what will be stored. Lunch overtime cannot be told from two times and stays typed. */
const otFromTimes = (date, start, end) => {
  const mins = (x) => { const m = /^(\d{1,2}):(\d{2})/.exec(x || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
  const s0 = mins(start);
  const e = mins(end);
  if (s0 === null || e === null) return null;
  // An end before the start is most often a slip: no overtime until it is put right (the server does the same).
  if (e < s0) return { bad: true };
  const overlap = (a, b) => Math.max(0, Math.min(e, b) - Math.max(s0, a));
  const weekend = [0, 6].includes(wdOf(date));
  return {
    weekend, morning: weekend ? 0 : overlap(0, 360), evening: weekend ? 0 : overlap(1080, 1800),
    other: weekend ? e - s0 : 0, dinner: e > 1140,
  };
};

/* ======================= THE DAY (driver's screen, and the admin's Daily entry) ======================= */
function DayForm({ vehicleId, date, setDate, isAdmin, t, w, onSaved }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);   // { leg, side } while the new-place box is open
  const [np, setNp] = useState({ name: '', address: '' });

  const load = useCallback(async () => {
    setData(null); setF(null);
    try {
      const j = await call(`${API}/day?vehicle=${encodeURIComponent(vehicleId || '')}&date=${date}`);
      setData(j);
      const l = j.log;
      setF(l ? {
        startTime: l.startTime || '', endTime: l.endTime || '', startKm: l.startKm ?? '', endKm: l.endKm ?? '',
        trips: l.trips.length ? l.trips : [{ from: j.prevLastPlace || '', to: '' }],
        fuel: l.fuel ?? '',
        // An other cost saved before kinds existed keeps its place with no kind (`legacy`) until somebody picks one.
        others: (l.others || []).map((o) => ({ typeId: o.typeId || '', amount: o.amount ?? '', note: o.note || '', legacy: !o.typeId })),
        remark: l.remark || '',
        // Lunch overtime is a time range now; a day saved before keeps its minutes (shown, and kept on save) until a
        // range replaces it or somebody clears it.
        lunchFrom: l.lunchFrom || '', lunchTo: l.lunchTo || '', lunchKeep: !l.lunchFrom && l.otLunch ? l.otLunch : 0,
      } : {
        /* A new weekday starts at 06:00 (Max, 2026-10-21, for Hue: the drivers should know it is normally 6:00 — "chứ ko là
           mấy anh sẽ nhập 5h59 hay 5h40"). A Saturday or Sunday is all overtime, so it starts empty: a start nobody
           chose would be paid. */
        startTime: [0, 6].includes(wdOf(date)) ? '' : '06:00', endTime: '', startKm: j.prevEndKm ?? '', endKm: '',
        trips: [{ from: j.prevLastPlace || '', to: '' }], fuel: '', others: [],
        remark: '', lunchFrom: '', lunchTo: '', lunchKeep: 0,
      });
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  }, [vehicleId, date]);
  useEffect(() => { setMsg(null); load(); }, [load]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v && v.target ? v.target.value : v }));
  // Other costs: one line each, with its kind (Max, 2026-10-21: "chọn điền chi phí và cho loại chi phí").
  const [newType, setNewType] = useState(null);   // { line, name } while a new kind is being typed
  const setCost = (i, k, v) => setF((p) => ({ ...p, others: p.others.map((o, j) => (j === i ? { ...o, [k]: v, ...(k === 'typeId' ? { legacy: false } : {}) } : o)) }));
  const addCost = () => setF((p) => ({ ...p, others: [...p.others, { typeId: '', amount: '', note: '', legacy: false }] }));
  const dropCost = (i) => setF((p) => ({ ...p, others: p.others.filter((_, j) => j !== i) }));
  const pickType = (i) => (e) => {
    if (e.target.value === '__new') { setNewType({ line: i, name: '' }); return; }
    setCost(i, 'typeId', e.target.value ? Number(e.target.value) : '');
  };
  const addType = async () => {
    const name = (newType?.name || '').trim();
    if (!name) return;
    try {
      const j = await call(`${API}/cost-types`, 'POST', { name });
      setData((d) => ({ ...d, costTypes: d.costTypes.some((x) => x.id === j.type.id) ? d.costTypes : [...d.costTypes, j.type] }));
      setCost(newType.line, 'typeId', j.type.id);
      setNewType(null);
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };
  const setLeg = (i, k, v) => setF((p) => {
    const trips = p.trips.map((x, j) => (j === i ? { ...x, [k]: v } : x));
    // The next leg starts where this one ended, unless somebody already chose otherwise.
    if (k === 'to' && trips[i + 1] && !trips[i + 1].from) trips[i + 1] = { ...trips[i + 1], from: v };
    return { ...p, trips };
  });
  const addLeg = () => setF((p) => ({ ...p, trips: [...p.trips, { from: p.trips[p.trips.length - 1]?.to || '', to: '' }] }));
  const dropLeg = (i) => setF((p) => ({ ...p, trips: p.trips.length > 1 ? p.trips.filter((_, j) => j !== i) : [{ from: '', to: '' }] }));
  const pick = (i, side) => (e) => {
    if (e.target.value === '__new') { setAdding({ leg: i, side }); setNp({ name: '', address: '' }); return; }
    setLeg(i, side, e.target.value);
  };
  const addPlace = async () => {
    if (!np.name.trim()) { setMsg({ kind: 'err', text: t('need_place_name') }); return; }
    try {
      const j = await call(`${API}/places`, 'POST', np);
      setData((d) => ({ ...d, places: d.places.some((p) => p.place_id === j.place.place_id) ? d.places : [...d.places, j.place] }));
      setLeg(adding.leg, adding.side, j.place.place_id);
      setAdding(null);
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };
  const save = async () => {
    if (f.others.some((o) => Number(o.amount) > 0 && !o.typeId && !o.legacy)) { setMsg({ kind: 'err', text: t('need_cost_type') }); return; }
    const lunch = lunchFromRange(f.lunchFrom, f.lunchTo);
    if (lunch && (lunch.half || lunch.bad)) { setMsg({ kind: 'err', text: t(lunch.half ? 'lunch_half' : 'lunch_order') }); return; }
    setBusy(true); setMsg(null);
    try {
      const j = await call(`${API}/day`, 'PUT', { vehicleId: data.vehicle.vehicle_id, date, ...f });
      setMsg(j.warnings && j.warnings.length ? { kind: 'warn', text: t('saved_warn') + j.warnings.join(' ') } : { kind: 'ok', text: t('saved_km', n0(j.kmMoved)) });
      await load();
      if (onSaved) onSaved();
    } catch (e) { setMsg({ kind: 'err', text: e.message }); } finally { setBusy(false); }
  };

  const today = data?.today;
  const moved = f && f.startKm !== '' && f.endKm !== '' ? Number(f.endKm) - Number(f.startKm) : null;
  const locked = !!data?.locked;
  const placeName = (p) => `${p.name}${p.status === 'pending' ? t('new_tag') : ''}`;
  /* What the overtime card shows: the rule on the times as typed — or, for a day imported from the old logs whose times
     have not been touched, the overtime recorded then (the server keeps it the same way). */
  const kept = !!(data?.log?.importRef && f && data.log.startTime === f.startTime && data.log.endTime === f.endTime);
  const ot = !f ? null : kept
    ? { weekend: [0, 6].includes(wdOf(date)), morning: data.log.otMorning || 0, evening: data.log.otEvening || 0, other: data.log.otOther || 0, dinner: !!data.log.dinner }
    : otFromTimes(date, f.startTime, f.endTime);
  const otVal = (m) => (m ? hm(m) : '0:00');
  const lunch = f ? lunchFromRange(f.lunchFrom, f.lunchTo) : null;

  return (
    <div className="dl-day">
      <div className="dl-datebar">
        <button type="button" className="dl-arrow" aria-label="-1" onClick={() => setDate(addDays(date, -1))}><ChevronLeft size={26} /></button>
        <label className="dl-date">
          <span className="dl-wd">{w[wdOf(date)]}</span>
          <span className="dl-dmy">{dmy(date)}</span>
          <input type="date" value={date} max={today || undefined} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label={t('th_date')} />
        </label>
        <button type="button" className="dl-arrow" aria-label="+1" disabled={!today || date >= today} onClick={() => setDate(addDays(date, 1))}><ChevronRight size={26} /></button>
      </div>
      {today && date !== today && <button type="button" className="dl-today" onClick={() => setDate(today)}>{t('notToday')}</button>}

      {!data || !f ? <div className="dl-loading"><Loader2 size={20} className="pa-spin" /> {t('loading')}</div> : (
        <>
          <div className={`dl-state${locked ? ' locked' : data.exists ? ' saved' : ''}`} data-testid="dl-state">
            {locked ? <><Lock size={16} /> {t('locked')}{isAdmin ? t('locked_admin') : ''}</>
              : data.exists ? t('exists', new Date(data.log.updatedAt).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }))
                : t('fresh')}
          </div>
          {data.log?.importRef && <div className="dl-hint dl-imported" data-testid="dl-imported">{t('imported_from', data.log.importRef)}</div>}
          <fieldset className="dl-fields" disabled={locked || busy}>
            <section className="dl-card">
              <h3>{t('time')}</h3>
              <div className="dl-two">
                <label><span>{t('start_time')}</span><TimeBox value={f.startTime} onChange={set('startTime')} testid="dl-start-time" t={t} /></label>
                <label><span>{t('end_time')}</span><TimeBox value={f.endTime} onChange={set('endTime')} testid="dl-end-time" t={t} /></label>
              </div>
              {![0, 6].includes(wdOf(date)) && <div className="dl-hint" data-testid="dl-start-hint">{t('start_hint')}</div>}
            </section>
            {/* OVERTIME (Hue's sheet columns, 2026-10-20): what she totals by hand for the drivers each month. */}
            {/* OVERTIME: worked out from the times, not typed (Max, 2026-10-21) — except at lunch, which two times cannot tell. */}
            <section className="dl-card" data-testid="dl-ot-card">
              <h3>{t('ot')}</h3>
              <div className="dl-hint dl-ot-rule">{kept ? t('ot_kept') : t('ot_rule')}</div>
              {/* Nothing until both times are in (Max, 2026-10-21: "lúc đầu ko cần chỗ này") — a new day opens with only the
                  start, and a box asking for the end time beside the box that takes it said nothing. An end before the start
                  still says so: that one is a mistake to put right. */}
              {!ot ? null : ot.bad ? <div className="dl-ot-none" data-testid="dl-ot-none">{t('ot_bad_times')}</div> : (
                <div className="dl-ot-calc">
                  {ot.weekend ? (
                    <div className="dl-ot-row"><span>{t('ot_weekend')}</span><b data-testid="dl-ot-weekend">{otVal(ot.other)}</b></div>
                  ) : (
                    <>
                      <div className="dl-ot-row"><span>{t('ot_morning')}</span><b data-testid="dl-ot-morning">{otVal(ot.morning)}</b></div>
                      <div className="dl-ot-row"><span>{t('ot_evening')}</span><b data-testid="dl-ot-evening">{otVal(ot.evening)}</b></div>
                    </>
                  )}
                  <div className={`dl-ot-row dl-ot-dinner${ot.dinner ? ' on' : ''}`} data-testid="dl-dinner" data-on={ot.dinner ? '1' : '0'}>
                    <span>{t('dinner')}<small>{t('dinner_hint')}</small></span><b>{ot.dinner ? '✓' : '—'}</b>
                  </div>
                </div>
              )}
              <div className="dl-lunch" data-testid="dl-ot-lunch">
                <span className="dl-lunch-label">{t('ot_lunch')}</span>
                <div className="dl-two">
                  <label><span>{t('from')}</span><TimeBox value={f.lunchFrom} onChange={set('lunchFrom')} hours={LUNCH_HOURS} testid="dl-lunch-from" t={t} /></label>
                  <label><span>{t('to')}</span><TimeBox value={f.lunchTo} onChange={set('lunchTo')} hours={LUNCH_HOURS} testid="dl-lunch-to" t={t} /></label>
                </div>
                {lunch && lunch.min !== undefined ? (
                  <div className="dl-lunch-calc" data-testid="dl-lunch-calc"><span>{t('lunch_calc', otVal(lunch.min))}</span>
                    <button type="button" className="dl-linkbtn" onClick={() => setF((p) => ({ ...p, lunchFrom: '', lunchTo: '' }))}>{t('clear_time')}</button></div>
                ) : lunch && (lunch.half || lunch.bad) ? (
                  <div className="dl-lunch-calc bad" data-testid="dl-lunch-calc">{t(lunch.half ? 'lunch_half' : 'lunch_order')}</div>
                ) : f.lunchKeep ? (
                  <div className="dl-lunch-calc" data-testid="dl-lunch-legacy"><span>{t('lunch_legacy', hm(f.lunchKeep))}</span>
                    <button type="button" className="dl-linkbtn" onClick={() => setF((p) => ({ ...p, lunchKeep: 0 }))}>{t('clear_time')}</button></div>
                ) : null}
                <div className="dl-hint">{t('ot_lunch_hint')}</div>
              </div>
            </section>
            <section className="dl-card">
              <h3>{t('odo')}</h3>
              <div className="dl-two">
                <label><span>{t('start_km')}</span><NumBox value={f.startKm} onChange={set('startKm')} data-testid="dl-start-km" /></label>
                <label><span>{t('end_km')}</span><NumBox value={f.endKm} onChange={set('endKm')} data-testid="dl-end-km" /></label>
              </div>
              {data.prevEndKm !== null && <div className="dl-hint">{t('prev_km', n0(data.prevEndKm), dmy(data.prevDate))}</div>}
              {moved !== null && <div className={`dl-moved${moved < 0 ? ' bad' : ''}`} data-testid="dl-moved">{t('moved', n0(moved))}</div>}
            </section>
            <section className="dl-card">
              <h3>{t('route')}</h3>
              {f.trips.map((leg, i) => (
                <div className="dl-leg" key={i}>
                  <div className="dl-leg-head"><span>{t('leg', i + 1)}</span>
                    {f.trips.length > 1 && <button type="button" className="dl-x" onClick={() => dropLeg(i)} aria-label={t('remove')}><X size={18} /></button>}
                  </div>
                  {['from', 'to'].map((side) => (
                    <label key={side} className="dl-place"><span>{t(side)}</span>
                      <select value={leg[side] || ''} onChange={pick(i, side)} data-testid={`dl-${side}-${i}`}>
                        <option value="">{!leg[side] && leg[`${side}Text`] ? `${leg[`${side}Text`]} ${t('typed_tag')}` : t('choose')}</option>
                        {data.places.map((p) => <option key={p.place_id} value={p.place_id}>{placeName(p)}</option>)}
                        <option value="__new">{t('new_place_opt')}</option>
                      </select>
                    </label>
                  ))}
                  {adding && adding.leg === i && (
                    <div className="dl-newplace">
                      <b>{t('np_title')}</b>
                      <input value={np.name} placeholder={t('place_name')} onChange={(e) => setNp({ ...np, name: e.target.value })} autoFocus />
                      <input value={np.address} placeholder={t('addr_opt')} onChange={(e) => setNp({ ...np, address: e.target.value })} />
                      <div className="dl-row-btns">
                        <button type="button" className="dl-btn light" onClick={() => setAdding(null)}>{t('cancel')}</button>
                        <button type="button" className="dl-btn" onClick={addPlace}>{t('add')}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" className="dl-add" onClick={addLeg}>{t('add_trip')}</button>
            </section>
            <section className="dl-card">
              <h3>{t('costs')}</h3>
              <label><span>{t('fuel')}</span><NumBox value={f.fuel} onChange={set('fuel')} data-testid="dl-fuel" /></label>
              <div className="dl-costs" data-testid="dl-other-costs">
                <h4>{t('other_costs')}</h4>
                {f.others.map((o, i) => (
                  <div className="dl-cost" key={i} data-testid={`dl-cost-${i}`}>
                    <div className="dl-leg-head"><span>{t('cost_line', i + 1)}</span>
                      <button type="button" className="dl-x" onClick={() => dropCost(i)} aria-label={t('remove')}><X size={18} /></button>
                    </div>
                    <label><span>{t('cost_type')}</span>
                      <select value={o.typeId || ''} onChange={pickType(i)} data-testid={`dl-cost-type-${i}`}>
                        <option value="">{o.legacy && !o.typeId ? t('untyped') : t('cost_choose')}</option>
                        {(data.costTypes || []).map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                        <option value="__new">{t('cost_new_opt')}</option>
                      </select>
                    </label>
                    {newType && newType.line === i && (
                      <div className="dl-newplace">
                        <input value={newType.name} placeholder={t('cost_new_ph')} autoFocus maxLength={80} data-testid="dl-cost-new-name"
                          onChange={(e) => setNewType({ ...newType, name: e.target.value })} />
                        <div className="dl-row-btns">
                          <button type="button" className="dl-btn light" onClick={() => setNewType(null)}>{t('cancel')}</button>
                          <button type="button" className="dl-btn" onClick={addType} data-testid="dl-cost-new-add">{t('add')}</button>
                        </div>
                      </div>
                    )}
                    <label><span>{t('cost_amount')}</span><NumBox value={o.amount} onChange={(v) => setCost(i, 'amount', v)} data-testid={`dl-cost-amount-${i}`} /></label>
                    <label><span>{t('cost_note')}</span><input value={o.note} maxLength={400} onChange={(e) => setCost(i, 'note', e.target.value)} data-testid={`dl-cost-note-${i}`} /></label>
                  </div>
                ))}
                <button type="button" className="dl-add" onClick={addCost} data-testid="dl-add-cost">{t('add_cost')}</button>
              </div>
            </section>
            <section className="dl-card">
              <h3>{t('remark')}</h3>
              <textarea rows={2} value={f.remark} placeholder={t('remark_ph')} onChange={set('remark')} maxLength={400} data-testid="dl-remark" />
            </section>
          </fieldset>
          {msg && <div className={`dl-msg ${msg.kind}`} data-testid="dl-msg">{msg.text}</div>}
          <div className="dl-savebar">
            <button type="button" className="dl-save" onClick={save} disabled={locked || busy} data-testid="dl-save">
              {busy ? <><Loader2 size={20} className="pa-spin" /> {t('saving')}</> : <><Save size={20} /> {t('save')}</>}
            </button>
          </div>
        </>
      )}
      {!f && msg && <div className={`dl-msg ${msg.kind}`}>{msg.text}</div>}
    </div>
  );
}

/* The driver's whole screen: a slim bar and the day. Nothing of the Portal around it — on a phone the sidebar would
   be the first thing to tap by mistake. */
/* `cars` and `onCar` are the admin's preview only (Max, 2026-10-19: "lúc mà xem như tài xế thì cần có thêm trường xe
   để chọn nhé"): the bar above the driver's screen picks whose screen it is, without going back to the admin tabs. A
   driver's own screen has neither — a driver has one car. */
/* The page behind a light driver screen stays light in a dark portal. It was `html[data-theme=dark]:has(.dl-phone.dl-light)
   body`, and Chrome re-checks an html-level :has() on every change anywhere in the page — about two seconds of the Sales
   Report's load in dark mode, on a screen that has nothing to do with drivers. So the screen marks <html> while it is
   mounted instead. A count, because the wrapper and the driver screen can both hold it at once. */
let lightRoots = 0;
function useLightRoot(on) {
  useEffect(() => {
    if (!on) return undefined;
    lightRoots += 1;
    document.documentElement.classList.add('dl-light-root');
    return () => {
      lightRoots -= 1;
      if (!lightRoots) document.documentElement.classList.remove('dl-light-root');
    };
  }, [on]);
}

function DriverScreen({ me, vehicleId, plate, driverName, lang, barLang, onExit, cars, onCar }) {
  const { logout } = useAuth();
  useLightRoot(true);
  const bar = barLang || lang;
  const [date, setDate] = useState(me.today);
  return (
    <div className="dl-phone dl-light" data-testid="dl-driver">
      {onExit && (
        <div className="dl-preview">
          <div className="dl-pv-line"><Eye size={16} /> <span>{bar.t('pv_label')} <b>{driverName || '—'}</b>. {bar.t('pv_note')}</span>
            <button type="button" onClick={onExit}>{bar.t('pv_exit')}</button></div>
          {cars && onCar && (
            <label className="dl-pv-car">{bar.t('car')}
              <select value={vehicleId} onChange={(e) => onCar(e.target.value)} data-testid="dl-preview-car">
                {cars.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate} · {v.driver_name || '—'}{v.active ? '' : ` (${bar.t('st_inactive')})`}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      <header className="dl-top">
        <div className="dl-who"><b>{lang.t('title')}</b><span>{plate} · {driverName}</span></div>
        {!onExit && <button type="button" className="dl-out" onClick={logout} aria-label={lang.t('logout')}><LogOut size={20} /></button>}
      </header>
      <main className="dl-main">
        <DayForm vehicleId={vehicleId} date={date} setDate={setDate} isAdmin={!!onExit} t={lang.t} w={lang.w} />
      </main>
    </div>
  );
}

/* ======================= ADMIN ======================= */
const ym0 = (d) => d.slice(0, 7);
const addMonths = (ym, k) => { const [y, m] = ym.split('-').map(Number); const x = new Date(Date.UTC(y, m - 1 + k, 1)); return x.toISOString().slice(0, 7); };

/* THE MONTH AT A GLANCE (Max, 2026-10-19, on a card per car listing only the logged days: "nếu mà xem 1 tháng cho 31
   ngày thì để như vầy tôi nghĩ là ko phù hợp"). Two parts: one summary row per car, which also picks the car; and that
   car's month as a CALENDAR — every day a row, so a missing weekday is a visible gap (amber), a weekend reads as one
   (grey), days still to come are dimmed, and the month adds up at the foot. A day opens Daily entry on that date. */
function Overview({ t, w, today, onEdit, onLoaded }) {
  const [params, setParams] = useSearchParams();
  const [month, setMonth] = useState(ym0(today));
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { setD(null); call(`${API}/admin/overview?month=${month}`).then(setD).catch((e) => setErr(e.message)); }, [month]);
  // tells the guide's button the cars are drawn (AdminScreen passes its state setter, which never changes)
  useEffect(() => { if (d && onLoaded) onLoaded(true); }, [d, onLoaded]);
  const cars = d?.cars || [];
  const sel = cars.find((c) => c.vehicleId === params.get('car')) || cars[0] || null;
  const pick = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set('car', id); return n; }, { replace: true });
  const status = (c) => <span className={`dl-chip ${c.status}`}>{t(c.status === 'closed' ? 'closed' : c.status === 'reopened' ? 'reopened' : 'open')}</span>;
  const perKm = (c) => (c.kmTotal ? n0(Math.round(c.fuel / c.kmTotal)) : '—');
  /* A day's overtime in one cell: "Tối 1:10 · Trưa 1:00 · Cơm" — the parts that are there. */
  const otOf = (r) => [r.otMorning ? `${t('ot_morning_s')} ${hm(r.otMorning)}` : '', r.otLunch ? `${t('ot_lunch_s')} ${hm(r.otLunch)}` : '',
    r.otEvening ? `${t('ot_evening_s')} ${hm(r.otEvening)}` : '', r.otOther ? `${t('ot_other_s')} ${hm(r.otOther)}` : '',
    r.dinner ? `🍚` : ''].filter(Boolean).join(' · ');
  const otMin = (c) => (c.otMorning || 0) + (c.otLunch || 0) + (c.otEvening || 0) + (c.otOther || 0);
  const otTotal = (c) => (otMin(c) ? hm(otMin(c)) : '0:00');

  /* Every day of the month, with the car's logged row where there is one. */
  const days = [];
  if (sel) {
    const [y, m] = month.split('-').map(Number);
    const n = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const byDate = new Map(sel.rows.map((r) => [r.date, r]));
    const missing = new Set(sel.missing);
    for (let i = 1; i <= n; i++) {
      const date = `${month}-${String(i).padStart(2, '0')}`;
      const wd = wdOf(date);
      const row = byDate.get(date) || null;
      const kind = row ? 'logged' : date > today ? 'future' : missing.has(date) ? 'missing' : (wd === 0 || wd === 6) ? 'weekend' : 'blank';
      days.push({ date, wd, row, kind });
    }
  }
  return (
    <div>
      <div className="dl-toolbar">
        <button type="button" className="pa-btn" onClick={() => setMonth(addMonths(month, -1))}><ChevronLeft size={14} /></button>
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="dl-monthbox" data-testid="dl-ov-month" />
        <button type="button" className="pa-btn" onClick={() => setMonth(addMonths(month, 1))} disabled={month >= ym0(today)}><ChevronRight size={14} /></button>
        {d && d.gasPrice ? <span className="pa-muted">{t('gas_short')}: {n0(d.gasPrice)} {t('vnd_l')}</span> : null}
      </div>
      {err && <div className="pa-banner err">{err}</div>}
      {d && d.beforeStart && <div className="pa-banner" data-testid="dl-before-start">{t('before_start', d.startMonth)}</div>}
      {!d ? <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('loading')}</div> : (
        <>
          <div className="pa-section">
            <table className="pa-table dl-sumtable" data-testid="dl-ov-cars">
              <thead><tr><th>{t('car')}</th><th>{t('th_driver')}</th><th>{t('th_status')}</th><th className="num">{t('days_logged')}</th>
                <th className="num">{t('th_missing')}</th><th className="num">{t('km')}</th><th className="num">{t('th_fuel')} (VND)</th>
                <th className="num">{t('other_short')}</th><th className="num">{t('vnd_per_km')}</th>
                <th className="num">{t('th_ot')}</th><th className="num">{t('th_dinner')}</th></tr></thead>
              <tbody>{cars.map((c) => (
                <tr key={c.vehicleId} className={`dl-click${sel && sel.vehicleId === c.vehicleId ? ' dl-sel' : ''}`} onClick={() => pick(c.vehicleId)}
                  data-testid={`dl-car-${c.vehicleId}`} aria-selected={sel && sel.vehicleId === c.vehicleId}>
                  <td><b>{c.plate}</b></td><td>{c.driverName || '—'}</td><td>{status(c)}</td>
                  <td className="num">{c.daysLogged}</td>
                  <td className="num">{c.missing.length ? <span className="dl-misscount">{c.missing.length}</span> : '0'}</td>
                  <td className="num">{n0(c.kmTotal)}</td><td className="num">{n0(c.fuel)}</td><td className="num">{n0(c.other)}</td><td className="num">{perKm(c)}</td>
                  <td className="num" title={`${t('ot_morning_s')} ${hm(c.otMorning) || '0:00'} · ${t('ot_lunch_s')} ${hm(c.otLunch) || '0:00'} · ${t('ot_evening_s')} ${hm(c.otEvening) || '0:00'} · ${t('ot_other_s')} ${hm(c.otOther) || '0:00'}`}>{otTotal(c)}</td>
                  <td className="num">{c.dinnerDays}</td>
                </tr>))}</tbody>
            </table>
          </div>
          {sel && (
            <div className="pa-section" data-testid="dl-ov-month-table">
              <div className="dl-carhead">
                <h3>{sel.plate} <span className="pa-muted">· {sel.driverName || '—'} · {month.slice(5)}/{month.slice(0, 4)}</span></h3>
                {status(sel)}
                <span className="dl-legend"><i className="lg-missing" />{t('not_logged')}<i className="lg-weekend" />{t('weekend')}</span>
                <button type="button" className="pa-btn" onClick={() => onEdit(sel.vehicleId, null)}>{t('edit_car')}</button>
              </div>
              <div className="dl-month-wrap">
                <table className="pa-table dl-table dl-monthtable">
                  <thead><tr><th>{t('th_date')}</th><th>{t('th_time')}</th><th>{t('th_route')}</th><th className="num">{t('th_start')}</th>
                    <th className="num">{t('th_end')}</th><th className="num">{t('km')}</th><th className="num">{t('th_fuel')}</th>
                    <th className="num">{t('th_other')}</th><th>{t('th_ot')}</th><th>{t('th_note')}</th><th>{t('th_by')}</th></tr></thead>
                  <tbody>{days.map(({ date, wd, row, kind }) => (
                    <tr key={date} className={`dl-day-${kind}${kind !== 'future' ? ' dl-click' : ''}`} data-testid={`dl-day-${date}`}
                      onClick={kind !== 'future' ? () => onEdit(sel.vehicleId, date) : undefined}>
                      <td className="pa-when"><b>{date.slice(8)}</b>/{date.slice(5, 7)} <span className="dl-wd2">{w[wd]}</span></td>
                      {row ? (
                        <>
                          <td className="pa-when">{[row.startTime, row.endTime].filter(Boolean).join('–')}</td>
                          <td>{row.route}</td><td className="num">{n0(row.startKm)}</td><td className="num">{n0(row.endKm)}</td>
                          <td className="num"><b>{n0(row.kmMoved)}</b></td><td className="num">{n0(row.fuel)}</td><td className="num">{n0(row.other)}</td>
                          <td className="pa-when" data-testid={`dl-ot-${date}`}>{otOf(row)}</td>
                          <td>{[row.remark, row.otherNote].filter(Boolean).join(' · ')}</td><td>{row.updatedBy}</td>
                        </>
                      ) : (
                        <td colSpan={10} className="dl-day-note">{kind === 'missing' ? t('not_logged') : kind === 'weekend' ? t('weekend') : ''}</td>
                      )}
                    </tr>))}</tbody>
                  <tfoot><tr>
                    <td><b>{t('total')}</b></td><td colSpan={4}>{t('total_days', sel.daysLogged, days.length)}</td>
                    <td className="num"><b>{n0(sel.kmTotal)}</b></td><td className="num"><b>{n0(sel.fuel)}</b></td><td className="num"><b>{n0(sel.other)}</b></td>
                    <td className="pa-when"><b>{otTotal(sel)}</b>{sel.dinnerDays ? <> · 🍚 {t('dinner_days', sel.dinnerDays)}</> : null}</td>
                    <td colSpan={2}>{t('vnd_per_km')}: {perKm(sel)}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Entry({ t, w, me, car, setCar, date, setDate }) {
  return (
    <div className="dl-entry">
      <div className="dl-toolbar">
        <label className="dl-inline">{t('car')}
          <select value={car} onChange={(e) => setCar(e.target.value)} data-testid="dl-entry-car">
            {me.vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate} · {v.driver_name || '—'}</option>)}
          </select>
        </label>
      </div>
      <div className="dl-entry-frame dl-vars"><DayForm vehicleId={car} date={date} setDate={setDate} isAdmin t={t} w={w} /></div>
    </div>
  );
}

/* A FORM IN A POPUP (Max, 2026-10-19: "những cái tạo này thì cho pop hết nhé", "sửa cũng vậy cần có popup"): over the
   Portal's header and sidebar like the Courier popups, closing only on its X or Cancel — a stray click outside must
   not throw away what was typed — with its own error line, so a refusal is read where the typing is. */
function DlModal({ title, onClose, onSave, t, busy, error, children, testid, saveLabel, narrow }) {
  return (
    <div className="dl-overlay" role="presentation">
      <div className={`dl-modal${narrow ? ' narrow' : ''}`} role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
        <div className="dl-modal-head">
          <h3>{title}</h3>
          <button type="button" className="dl-modal-x" onClick={onClose} aria-label={t('cancel')} title={t('cancel')}><X size={18} /></button>
        </div>
        <div className="dl-modal-body">{children}</div>
        {error && <div className="pa-banner err dl-modal-err" data-testid="dl-modal-err">{error}</div>}
        <div className="dl-modal-foot">
          <button type="button" className="pa-btn" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="pa-btn submit" onClick={onSave} disabled={busy} data-testid="dl-modal-save">
            {busy ? <Loader2 size={14} className="pa-spin" /> : null} {saveLabel || t('save')}</button>
        </div>
      </div>
    </div>
  );
}

/* FIFTEEN ROWS, THEN THE TABLE SCROLLS UNDER ITS HEAD (Max, 2026-10-19: "header có thêm sort và filter như excel /
   freeze/ stick header / bảng thì hiện 15 dòng thui và có thể scrolldown xuống"). MEASURED after each render — the
   head plus fifteen of the shortest rows, as the Users list does — since a row is as tall as whatever wraps in it. */
const VISIBLE_ROWS = 15;
function useRowBox(dep) {
  const ref = useRef(null);
  const [h, setH] = useState(null);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const head = box.querySelector('thead');
    const rows = [...box.querySelectorAll('tbody tr')].map((r) => r.getBoundingClientRect().height).filter(Boolean);
    const next = head && rows.length > VISIBLE_ROWS ? Math.ceil(head.getBoundingClientRect().height + VISIBLE_ROWS * Math.min(...rows)) + 1 : null;
    setH((prev) => (prev === next ? prev : next));
  }, [dep]);
  return [ref, h];
}

/* ONE TABLE SHAPE FOR THE ADMIN SCREEN (Max, 2026-10-19: "okie muốn á", to the Places table's Excel headers, frozen
   head and fifteen rows for the other four): Excel's sort and filter on every column (SortTh + useColumnTools, as Project
   Approval, GTM and Cash — a column sorts and filters on what its `get` returns, which is what its cell shows), "n / N
   rows · Clear filters" while filtered, the head pinned and the box fifteen rows tall. One component, so five tables
   cannot drift into five behaviours. `id` names the parts: <id>-table, <id>-box, <id>-filtered. */
function DlGrid({ t, id, rows, cols, rowKey, renderRow, tail = true }) {
  const tools = useColumnTools(rows || [], cols);
  const [boxRef, boxH] = useRowBox(`${rows ? 1 : 0}:${tools.shown.length}`);
  return (
    <>
      {tools.active && (
        <div className="dl-filterline" data-testid={`${id}-filtered`}>{t('rows_of', tools.shown.length, (rows || []).length)}
          <button type="button" className="pa-btn" onClick={tools.clear}>{t('fp_clear')}</button></div>)}
      <div className="dl-freeze" ref={boxRef} style={boxH ? { maxHeight: boxH } : undefined} data-testid={`${id}-box`}>
        <table className="pa-table" data-testid={`${id}-table`}>
          <thead><tr>{cols.map((c) => <SortTh key={c.key} label={c.label} className={c.num ? 'num' : ''} {...tools.thProps(c.key)} />)}
            {tail && <th />}</tr></thead>
          <tbody>{tools.shown.map((r) => <React.Fragment key={rowKey(r)}>{renderRow(r)}</React.Fragment>)}</tbody>
        </table>
      </div>
    </>
  );
}

function Places({ t }) {
  const [rows, setRows] = useState(null);
  const [ed, setEd] = useState(null);
  const [q, setQ] = useState('');
  const [find, setFind] = useState({ busy: false, msg: '' });
  const [msg, setMsg] = useState(null);
  const [edErr, setEdErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call(`${API}/admin/places`).then((j) => setRows(j.places)).catch((e) => setMsg({ bad: true, text: e.message })), []);
  useEffect(() => { load(); }, [load]);
  const open = (p) => { setEd(p ? { ...p } : { name: '', address: '', status: 'active', lat: null, lng: null }); setQ(''); setFind({ busy: false, msg: '' }); setEdErr(''); };
  const geocode = async () => {
    setFind({ busy: true, msg: t('searching') });
    try {
      const j = await call(`${API}/admin/geocode`, 'POST', { q });
      if (!j.ok) { setFind({ busy: false, msg: t('not_found') }); return; }
      setEd((e) => ({ ...e, lat: j.lat, lng: j.lng, address: e.address || j.address || '', name: e.name || j.name || '' }));
      setFind({ busy: false, msg: '' });
    } catch (e) { setFind({ busy: false, msg: e.message }); }
  };
  const save = async () => {
    setBusy(true); setEdErr('');
    try {
      if (ed.place_id) await call(`${API}/admin/places/${ed.place_id}`, 'PUT', ed);
      else await call(`${API}/admin/places`, 'POST', ed);
      setEd(null); setMsg({ text: t('saved') }); load();
    } catch (e) { setEdErr(e.message); } finally { setBusy(false); }
  };
  const st = (s) => t(`st_${s}`);
  /* Excel's sort and filter on every column (SortTh + useColumnTools, as Project Approval, GTM and Cash): a column
     sorts and filters on what its cell shows. */
  const cols = [
    { key: 'place_id', label: 'ID', get: (p) => p.place_id },
    { key: 'name', label: t('th_name'), get: (p) => p.name },
    { key: 'status', label: t('th_status'), get: (p) => st(p.status) },
    { key: 'address', label: t('th_addr'), get: (p) => p.address || '' },
    { key: 'uses', label: t('th_uses'), get: (p) => Number(p.uses) || 0, num: true },
    { key: 'map', label: t('th_map'), get: (p) => (p.lat !== null ? t('open_map') : t('no_coord')) },
  ];
  return (
    <div>
      {/* The label carries its own "+", so no icon beside it ("+ + Thêm địa điểm"). */}
      <div className="dl-toolbar"><button type="button" className="pa-btn submit" onClick={() => open(null)}>{t('pl_new')}</button></div>
      {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>{msg.text}</div>}
      {ed && (
        <DlModal title={ed.place_id ? `${t('pe_edit')} · ${ed.place_id}` : t('pe_new')} onClose={() => setEd(null)} onSave={save} t={t}
          busy={busy} error={edErr} testid="dl-place-editor">
          <div className="dl-grid two">
            <label>{t('th_name')}<input value={ed.name} onChange={(e) => setEd({ ...ed, name: e.target.value })} autoFocus /></label>
            <label>{t('th_status')}<select value={ed.status} onChange={(e) => setEd({ ...ed, status: e.target.value })}>
              {['active', 'pending', 'inactive'].map((s2) => <option key={s2} value={s2}>{st(s2)}</option>)}</select></label>
            <label className="wide">{t('th_addr')}<input value={ed.address || ''} onChange={(e) => setEd({ ...ed, address: e.target.value })} /></label>
            <label className="wide">{t('pe_search')}
              <div className="dl-findrow"><input value={q} placeholder={t('pe_search_ph')} onChange={(e) => setQ(e.target.value)} data-testid="dl-geocode-q" />
                <button type="button" className="pa-btn" onClick={geocode} disabled={!q.trim() || find.busy}><MapPin size={14} /> {t('find')}</button></div>
              <span className="dl-hint2">{find.msg || t('pe_help')}</span></label>
            <label className="wide">{t('pe_coord')}<input value={ed.lat !== null && ed.lat !== undefined ? `${ed.lat}, ${ed.lng}` : ''} readOnly data-testid="dl-coord" /></label>
          </div>
        </DlModal>
      )}
      {!rows ? <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('loading')}</div> : (
        <div className="pa-section">
          <DlGrid t={t} id="dl-places" rows={rows} cols={cols} rowKey={(p) => p.place_id} renderRow={(p) => (
              <tr>
                <td className="mono">{p.place_id}</td><td>{p.name}</td><td><span className={`dl-chip ${p.status}`}>{st(p.status)}</span></td>
                <td>{p.address || ''}</td><td className="num">{p.uses}</td>
                <td>{p.lat !== null ? <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer">{t('open_map')}</a> : <span className="pa-warn">{t('no_coord')}</span>}</td>
                <td><button type="button" className="pa-btn" onClick={() => open(p)}>{t('edit')}</button></td>
              </tr>)} />
        </div>
      )}
    </div>
  );
}

function People({ t }) {
  const [d, setD] = useState(null);
  const [ed, setEd] = useState(null);   // { kind: 'driver'|'car', row }
  const [msg, setMsg] = useState(null);
  const [edErr, setEdErr] = useState('');
  const [busy, setBusy] = useState(false);
  const open = (x) => { setEd(x); setEdErr(''); };
  const load = useCallback(() => call(`${API}/admin/people`).then(setD).catch((e) => setMsg({ bad: true, text: e.message })), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const r = ed.row;
    setBusy(true); setEdErr('');
    try {
      if (ed.kind === 'driver') {
        /* No account is sent: the server links the one whose address this is (Max: the account follows the email). */
        const body = { name: r.name, vehicleId: r.vehicle_id || '', email: r.email || '', active: r.active !== false && r.active !== 0 };
        await call(`${API}/admin/drivers${r.driver_id ? `/${r.driver_id}` : ''}`, r.driver_id ? 'PUT' : 'POST', body);
      } else {
        const body = { plate: r.plate, model: r.model || '', year: r.year ?? '', currentOdometer: r.current_odometer ?? '', driverId: r.driver_id || '', active: r.active !== false && r.active !== 0 };
        await call(`${API}/admin/vehicles${r.vehicle_id ? `/${r.vehicle_id}` : ''}`, r.vehicle_id ? 'PUT' : 'POST', body);
      }
      setEd(null); setMsg({ text: t('saved') }); load();
    } catch (e) { setEdErr(e.message); } finally { setBusy(false); }
  };
  const row = (k) => (e) => setEd((x) => ({ ...x, row: { ...x.row, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value } }));
  /* "Đang dùng" as a field like the others — a caption over a box as tall as an input — so it sits on the grid's
     lines instead of floating mid-row (Max, 2026-10-19: "canh lại cho nó đẹp nha"). */
  const activeBox = (
    <div className="dl-gfield">{t('status_lbl')}
      <label className="dl-checkbox"><input type="checkbox" checked={ed?.row.active !== false && ed?.row.active !== 0} onChange={row('active')}
        data-testid="dl-active" /> {t('active')}</label></div>);
  /* The Portal account FOLLOWS THE ADDRESS, as the server links it: the active account with that address, or none
     until the first Google sign-in. Said live as the address is typed. */
  const plateOf = (vid) => (d?.vehicles || []).find((v) => v.vehicle_id === vid)?.plate || '';
  const seesOf = (x) => (!x.portal_user_id || x.screen_only === null ? '' : x.screen_only ? t('only_driver') : t('keeps_portal'));
  const typed = String(ed?.row.email || '').trim().toLowerCase();
  const acct = typed ? (d?.users || []).find((u) => String(u.UserEmail || '').toLowerCase() === typed) : null;
  if (!d) return <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('loading')}</div>;
  return (
    <div>
      {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>{msg.text}</div>}
      {ed && (
        <DlModal t={t} onClose={() => setEd(null)} onSave={save} busy={busy} error={edErr} testid="dl-people-editor"
          title={ed.kind === 'driver' ? (ed.row.driver_id ? `${t('edit_driver')} · ${ed.row.driver_id}` : t('new_driver_t'))
            : (ed.row.vehicle_id ? `${t('edit_car_t')} · ${ed.row.vehicle_id}` : t('new_car_t'))}>
          {ed.kind === 'driver' ? (
            <div className="dl-grid two">
              <label>{t('th_name')}<input value={ed.row.name || ''} onChange={row('name')} autoFocus data-testid="dl-driver-name" /></label>
              <label>{t('car')}<select value={ed.row.vehicle_id || ''} onChange={row('vehicle_id')} data-testid="dl-driver-car"><option value="">—</option>
                {d.vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate}</option>)}</select></label>
              <label className="wide">{t('th_email')}<input value={ed.row.email || ''} onChange={row('email')} placeholder={t('email_ph')}
                  autoComplete="off" data-testid="dl-driver-email" />
                <span className={`dl-hint2${acct ? ' ok' : ''}`} data-testid="dl-driver-account">
                  {!typed ? t('email_hint') : acct ? t('acct_found', `${acct.UserName} · ${acct.UserEmail}`) : t('acct_none')}</span></label>
              {activeBox}
            </div>
          ) : (
            <div className="dl-grid two">
              <label>{t('th_plate')}<input value={ed.row.plate || ''} onChange={row('plate')} autoFocus data-testid="dl-car-plate" /></label>
              <label>{t('th_model')}<input value={ed.row.model || ''} onChange={row('model')} /></label>
              <label>{t('th_year')}<input value={ed.row.year ?? ''} onChange={row('year')} inputMode="numeric" /></label>
              <label>{t('th_odo')}<input value={ed.row.current_odometer ?? ''} onChange={row('current_odometer')} inputMode="numeric" /></label>
              <label>{t('th_driver')}<select value={ed.row.driver_id || ''} onChange={row('driver_id')}><option value="">—</option>
                {d.drivers.map((x) => <option key={x.driver_id} value={x.driver_id}>{x.name}</option>)}</select></label>
              {activeBox}
            </div>
          )}
        </DlModal>
      )}
      <div className="pa-section">
        {/* What Hue sends the drivers (Jay, 2026-10-20: drivers sign in with their own Google account). The page it
            opens shows a driver one card with one button. */}
        <p className="dl-note" data-testid="dl-drivers-link">{t('drivers_link')}: <b>{window.location.origin}/driver-log</b> — {t('drivers_link_how')}</p>
        <div className="dl-carhead"><h3>{t('drivers')}</h3><button type="button" className="pa-btn submit" onClick={() => open({ kind: 'driver', row: { active: true } })} data-testid="dl-new-driver">{t('new_driver')}</button></div>
        <DlGrid t={t} id="dl-drivers" rows={d.drivers} rowKey={(x) => x.driver_id} cols={[
          { key: 'driver_id', label: 'ID', get: (x) => x.driver_id },
          { key: 'name', label: t('th_name'), get: (x) => x.name },
          { key: 'car', label: t('car'), get: (x) => plateOf(x.vehicle_id) },
          { key: 'email', label: t('th_email'), get: (x) => x.email || '' },
          { key: 'account', label: t('th_account'), get: (x) => x.UserName || (x.email ? t('not_signed_in') : t('no_account')) },
          { key: 'sees', label: t('acct_roles'), get: (x) => seesOf(x) },
          { key: 'active', label: t('th_active'), get: (x) => (x.active ? t('yes') : t('no')) },
        ]} renderRow={(x) => {
            const roles = String(x.account_roles || '').split(',').map((r) => r.trim()).filter(Boolean);
            return (
            <tr data-testid={`dl-driver-${x.driver_id}`}><td className="mono">{x.driver_id}</td><td>{x.name}</td>
              <td>{plateOf(x.vehicle_id) || '—'}</td>
              <td>{x.email || <span className="pa-warn">—</span>}</td>
              <td>{x.UserName || <span className="pa-muted">{x.email ? t('not_signed_in') : t('no_account')}</span>}</td>
              <td data-testid={`dl-driver-sees-${x.driver_id}`}>{!x.portal_user_id || x.screen_only === null ? '—' : x.screen_only
                ? <span className="dl-chip active">{t('only_driver')}</span>
                : <span className="dl-chip reopened" title={roles.join(', ')}>{t('keeps_portal')}</span>}</td>
              <td>{x.active ? t('yes') : t('no')}</td>
              <td><button type="button" className="pa-btn" onClick={() => open({ kind: 'driver', row: { ...x } })}>{t('edit')}</button></td></tr>);
          }} />
      </div>
      <div className="pa-section">
        <div className="dl-carhead"><h3>{t('cars')}</h3><button type="button" className="pa-btn submit" onClick={() => open({ kind: 'car', row: { active: true } })} data-testid="dl-new-car">{t('new_car')}</button></div>
        <DlGrid t={t} id="dl-cars" rows={d.vehicles} rowKey={(v) => v.vehicle_id} cols={[
          { key: 'vehicle_id', label: 'ID', get: (v) => v.vehicle_id },
          { key: 'plate', label: t('th_plate'), get: (v) => v.plate },
          { key: 'model', label: t('th_model'), get: (v) => v.model || '' },
          { key: 'year', label: t('th_year'), get: (v) => (v.year ? Number(v.year) : null), num: true },
          { key: 'odo', label: t('th_odo'), get: (v) => (v.current_odometer === null || v.current_odometer === undefined ? null : Number(v.current_odometer)), num: true },
          { key: 'driver', label: t('th_driver'), get: (v) => v.driver_name || '' },
          { key: 'active', label: t('th_active'), get: (v) => (v.active ? t('yes') : t('no')) },
        ]} renderRow={(v) => (
            <tr><td className="mono">{v.vehicle_id}</td><td>{v.plate}</td><td>{v.model || ''}</td><td className="num">{v.year || ''}</td>
              <td className="num">{n0(v.current_odometer)}</td><td>{v.driver_name || '—'}</td><td>{v.active ? t('yes') : t('no')}</td>
              <td><button type="button" className="pa-btn" onClick={() => open({ kind: 'car', row: { ...v } })}>{t('edit')}</button></td></tr>)} />
      </div>
    </div>
  );
}

/* GAS PRICE (Max, 2026-10-19): worked out from Petrolimex — the grade and zone in the settings, a month = the average
   of its days — and still typeable. A typed month wins and is marked; "Use the automatic price" hands it back. */
/* GAS PRICE (Max, 2026-10-19): each day's price from giaxanghomnay.com (one source), a month = the average of its days,
   still typeable in a popup. A typed month wins and is marked; "Use the automatic price" hands it back. */
function Gas({ t, w, today }) {
  const [d, setD] = useState(null);
  const [ed, setEd] = useState(null);   // the typed month in its popup
  const [edErr, setEdErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = (r) => { setEd(r ? { yearMonth: r.year_month, priceVnd: String(r.price_vnd), refDate: r.ref_date || '' }
    : { yearMonth: ym0(today), priceVnd: '', refDate: '' }); setEdErr(''); };
  const load = useCallback(() => call(`${API}/admin/gas`).then(setD).catch((e) => setMsg({ bad: true, text: e.message })), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setEdErr('');
    try { await call(`${API}/admin/gas`, 'PUT', ed); setEd(null); setMsg({ text: t('saved') }); load(); } catch (e) { setEdErr(e.message); }
  };
  const sync = async () => {
    setBusy(true); setMsg(null);
    try {
      const j = await call(`${API}/admin/gas/sync`, 'POST', {});
      const probs = (j.problems || []).map((x) => `${t(`gas_src_${x.source}`)}: ${x.message}`);
      setMsg(probs.length ? { bad: true, text: probs.join(' · ') } : { text: t('gas_synced', j.fetched, n0(j.dailyToday)) }); load();
    } catch (e) { setMsg({ bad: true, text: e.message }); } finally { setBusy(false); }
  };
  const backToAuto = async (ym) => {
    try { await call(`${API}/admin/gas/${ym}`, 'DELETE'); setMsg({ text: t('gas_auto_back', ym) }); load(); } catch (e) { setMsg({ bad: true, text: e.message }); }
  };
  const fuel = d?.fuel;
  /* The price in force and the day it STARTED: walk back while the days carry the same price. */
  const now = (() => {
    const dd = fuel?.daily || [];
    if (!dd.length) return null;
    let k = 0;
    while (k + 1 < dd.length && dd[k + 1].price === dd[0].price) k += 1;
    return { price: dd[0].price, day: dd[k].day };
  })();
  const failing = Object.entries(fuel?.failing || {});
  const last = String(fuel?.lastSync || '');
  const lastAt = last.slice(0, 24);
  const lastErr = last.includes(' error:') ? last.slice(last.indexOf(' error:') + 1) : '';
  const rows = d?.prices;
  return (
    <div>
      <div className="pa-section dl-editor" data-testid="dl-gas-auto">
        <h3>{t('gas_auto_title')}</h3>
        {fuel && (
          <div className="dl-gas-now">
            <div><span>{t('gas_grade')}</span><b>{fuel.grade} · {t('gas_zone', fuel.zone)}</b></div>
            <div><span>{t('gas_current')}</span><b data-testid="dl-gas-current">{now ? `${n0(now.price)} ${t('vnd_l')}` : '—'}</b>
              {now && <small>{t('gas_since', dmy(now.day))}</small>}</div>
            <div><span>{t('gas_last_sync')}</span><b>{lastAt ? new Date(lastAt).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</b>
              {lastErr && <small className="pa-warn">{lastErr}</small>}</div>
          </div>
        )}
        {failing.length > 0 && (
          <div className="pa-banner err" data-testid="dl-gas-failing">
            {failing.map(([src, x]) => <div key={src}><b>{t(`gas_src_${src}`)}</b>: {x.message} — {t('gas_failing_since',
              new Date(x.since).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }), x.runs)}
              {x.mailedAt ? ` · ${t('gas_mailed')}` : ''}</div>)}
          </div>
        )}
        <p className="dl-note">{t('gas_rule')}</p>
        <div className="dl-row-btns" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="pa-btn submit" onClick={sync} disabled={busy} data-testid="dl-gas-sync">
            {busy ? <Loader2 size={14} className="pa-spin" /> : null} {t('gas_sync')}</button>
        </div>
      </div>
      {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`} data-testid="dl-gas-msg">{msg.text}</div>}
      {ed && (
        <DlModal title={t('gas_title_one')} onClose={() => setEd(null)} onSave={save} t={t} error={edErr} testid="dl-gas-editor">
          <p className="dl-note" style={{ marginTop: 0 }}>{t('gas_type_hint')}</p>
          <div className="dl-grid two">
            <label>{t('month')}<input type="month" value={ed.yearMonth} onChange={(e) => setEd({ ...ed, yearMonth: e.target.value })} /></label>
            <label>{t('gas_price')}<NumBox value={ed.priceVnd} onChange={(v) => setEd({ ...ed, priceVnd: v })} autoFocus data-testid="dl-gas-price" /></label>
            <label>{t('ref_date')}<input type="date" value={ed.refDate} onChange={(e) => setEd({ ...ed, refDate: e.target.value })} /></label>
          </div>
        </DlModal>
      )}
      <div className="pa-section">
        <div className="dl-carhead"><h3>{t('gas_title')}</h3>
          <button type="button" className="pa-btn submit" onClick={() => open(null)} data-testid="dl-gas-new">{t('gas_new')}</button></div>
        {!rows ? <Loader2 size={16} className="pa-spin" /> : !rows.length ? <div className="pa-empty">{t('no_data')}</div> : (
          <DlGrid t={t} id="dl-gas-months" rows={rows} rowKey={(r) => r.year_month} cols={[
            { key: 'year_month', label: t('month'), get: (r) => r.year_month },
            { key: 'price', label: t('gas_price'), get: (r) => Number(r.price_vnd), num: true },
            { key: 'source', label: t('gas_source'), get: (r) => (r.source === 'Petrolimex' ? t('gas_src_auto', r.days_counted, r.days_in_month) : t('gas_src_typed')) },
            { key: 'ref_date', label: t('ref_date'), get: (r) => (r.ref_date ? dmy(r.ref_date) : '') },
            { key: 'updated_by', label: t('th_by'), get: (r) => r.updated_by || '' },
          ]} renderRow={(r) => (
              <tr data-testid={`dl-gas-${r.year_month}`}>
                <td>{r.year_month}</td><td className="num">{n0(r.price_vnd)}</td>
                <td>{r.source === 'Petrolimex'
                  ? <span className="dl-chip active">{t('gas_src_auto', r.days_counted, r.days_in_month)}</span>
                  : <span className="dl-chip reopened">✎ {t('gas_src_typed')}</span>}</td>
                <td>{r.ref_date ? dmy(r.ref_date) : ''}</td><td>{r.updated_by}</td>
                <td className="dl-gas-acts">
                  <button type="button" className="pa-btn" onClick={() => open(r)}>{t('edit')}</button>
                  {r.source !== 'Petrolimex' && <button type="button" className="pa-btn" onClick={() => backToAuto(r.year_month)}>{t('gas_use_auto')}</button>}
                </td></tr>)} />
        )}
      </div>
      {fuel && fuel.daily && fuel.daily.length > 0 && (
        <div className="pa-section">
          <h3 className="dl-sub">{t('gas_daily_title', fuel.grade)}</h3>
          <div className="dl-daily" data-testid="dl-gas-daily">
            {fuel.daily.map((x, i) => {
              const prev = fuel.daily[i + 1];
              const moved = prev && prev.price !== x.price;
              return <div key={x.day} className={moved ? 'moved' : ''} title={moved ? `${n0(prev.price)} → ${n0(x.price)}` : ''}>
                <span>{dmy(x.day).slice(0, 5)} {w ? w[wdOf(x.day)] : ''}</span><b>{n0(x.price)}</b></div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* CLOSE / REOPEN A MONTH. Asked in the Portal's own popup, not the browser's OK / Cancel (Max, 2026-10-19: "tạo pop up
   riêng nha"), which says what will happen — the PDFs, who they are mailed to, the lock. And a month the Portal cannot
   close is said BEFORE the press, in Vietnamese, with the button dead: a month before driver_log.start_month was kept
   before the Portal's first month ("cảnh báo này là sao" — and Max, 2026-10-20: the Apps Script app was never used, so
   nothing was logged anywhere before it — the server's English refusal was the first anybody heard of it), and a
   month that has not ended cannot be closed yet. Reopen is live only where a car of that month is closed. */
function Close({ t, today, startMonth }) {
  const prev = addMonths(ym0(today), -1);
  const [rows, setRows] = useState(null);
  const [notifyTo, setNotifyTo] = useState('');
  const [month, setMonth] = useState(!startMonth || prev >= startMonth ? prev : startMonth);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(null);   // 'close' | 'reopen' while its popup is open
  const [askErr, setAskErr] = useState('');
  const load = useCallback(() => call(`${API}/admin/closings`).then((j) => { setRows(j.closings); setNotifyTo(j.notifyTo || ''); })
    .catch((e) => setMsg({ bad: true, text: e.message })), []);
  useEffect(() => { load(); }, [load]);
  const closeWhy = startMonth && month < startMonth ? t('close_before', month, startMonth)
    : month > prev ? t('close_not_ended', month) : '';
  const anyClosed = (rows || []).some((r) => r.year_month === month && r.status === 'closed');
  const reopenWhy = closeWhy && month < (startMonth || '') ? closeWhy : !anyClosed ? t('reopen_none', month) : '';
  const go = async () => {
    setBusy(true); setAskErr('');
    try {
      await call(`${API}/admin/${ask}`, 'POST', { yearMonth: month });
      setMsg({ text: t(ask === 'close' ? 'closed_msg' : 'reopened_msg', month) }); setAsk(null); load();
    } catch (e) { setAskErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div>
      {ask && (
        <DlModal t={t} narrow onClose={() => setAsk(null)} onSave={go} busy={busy} error={askErr} testid="dl-close-confirm"
          title={t(ask === 'close' ? 'close_t' : 'reopen_t', month)} saveLabel={t(ask === 'close' ? 'btn_close' : 'btn_reopen')}>
          <ul className="dl-asklist">
            {ask === 'close' ? <>
              <li>{t('close_body1', month)}</li>
              <li>{t('close_body2', notifyTo || '—')}</li>
              <li>{t('close_body3')}</li>
            </> : <>
              <li>{t('reopen_body1', month)}</li>
              <li>{t('reopen_body2')}</li>
            </>}
          </ul>
        </DlModal>
      )}
      <div className="pa-section dl-editor">
        <h3>{t('close_title')}</h3>
        <p className="dl-note">{t('close_hint')}</p>
        <div className="dl-toolbar">
          <input type="month" value={month} min={startMonth || undefined} max={prev} onChange={(e) => { if (e.target.value) { setMonth(e.target.value); setMsg(null); } }}
            className="dl-monthbox" data-testid="dl-close-month" />
          <button type="button" className="pa-btn submit" disabled={busy || !!closeWhy} title={closeWhy || undefined}
            onClick={() => { setAskErr(''); setAsk('close'); }} data-testid="dl-close">
            <FileText size={14} /> {t('btn_close')}</button>
          <button type="button" className="pa-btn" disabled={busy || !!reopenWhy} title={reopenWhy || undefined}
            onClick={() => { setAskErr(''); setAsk('reopen'); }} data-testid="dl-reopen">{t('btn_reopen')}</button>
        </div>
        {closeWhy && <div className="dl-why" data-testid="dl-close-why">{closeWhy}</div>}
        {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`} data-testid="dl-close-msg">{msg.text}</div>}
      </div>
      <div className="pa-section">
        {!rows ? <Loader2 size={16} className="pa-spin" /> : !rows.length ? <div className="pa-empty">{t('no_closings')}</div> : (
          <DlGrid t={t} id="dl-closings" rows={rows} tail={false} rowKey={(r) => r.closing_pk} cols={[
            { key: 'year_month', label: t('month'), get: (r) => r.year_month },
            { key: 'plate', label: t('car'), get: (r) => r.plate || '' },
            { key: 'status', label: t('th_status'), get: (r) => t(r.status === 'closed' ? 'closed' : 'reopened') },
            { key: 'days', label: t('days_logged'), get: (r) => Number(r.days_logged) || 0, num: true },
            { key: 'km', label: t('km'), get: (r) => Number(r.km_total) || 0, num: true },
            { key: 'fuel', label: t('th_fuel'), get: (r) => Number(r.fuel_vnd) || 0, num: true },
            { key: 'other', label: t('th_other'), get: (r) => Number(r.other_vnd) || 0, num: true },
            { key: 'vpk', label: t('vnd_per_km'), get: (r) => (r.vnd_per_km === null ? null : Number(r.vnd_per_km)), num: true },
            { key: 'gas', label: t('gas_short'), get: (r) => (r.gas_price_vnd === null ? null : Number(r.gas_price_vnd)), num: true },
            { key: 'pdf', label: t('pdf'), get: (r) => (r.pdf_file ? t('pdf') : '') },
          ]} renderRow={(r) => (
              <tr><td>{r.year_month}</td><td>{r.plate}</td>
                <td><span className={`dl-chip ${r.status}`}>{t(r.status === 'closed' ? 'closed' : 'reopened')}</span></td>
                <td className="num">{r.days_logged}</td><td className="num">{n0(r.km_total)}</td><td className="num">{n0(r.fuel_vnd)}</td>
                <td className="num">{n0(r.other_vnd)}</td><td className="num">{n0(r.vnd_per_km)}</td><td className="num">{n0(r.gas_price_vnd)}</td>
                <td>{r.pdf_file ? <a href={`${API}/admin/closings/${r.closing_pk}/pdf`} target="_blank" rel="noopener noreferrer">{t('pdf')}</a> : '—'}</td></tr>)} />
        )}
      </div>
    </div>
  );
}

/* THE REPORT (task #1191 — Hue, 03/11: "trang report để tích chọn các tháng … sếp muốn xem báo cáo tháng nào sếp chọn tháng
   đó, hoặc nửa năm, 1 năm, tùy sếp chọn"). The "12 months" table of Hue's monthly GW approval — km done, fuel paid, fuel per
   km, the month's gas price and its date — for any months the reader ticks, across years, one block per car, a chart of
   fuel per km against the gas price, and the same blocks as a PDF for the approval (Max 03/11: "nên là download pdf"). It opens on the twelve months
   up to last month, which is what the GW report carries. */
const RP_COLORS = ['#2563eb', '#f59e0b', '#10b981', '#8b5cf6'];
function Report({ t, lang, today }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const thisYm = ym0(today);
  const lastYm = addMonths(thisYm, -1);
  const range = (from, n) => [...Array(n).keys()].map((i) => addMonths(from, i));
  const [picked, setPicked] = useState(() => new Set(range(addMonths(lastYm, -11), 12)));
  const [year, setYear] = useState(Number(lastYm.slice(0, 4)));
  const [carSel, setCarSel] = useState('');
  useEffect(() => { call(`${API}/admin/report`).then(setData).catch((e) => setErr(e.message)); }, []);
  const mon = (T[lang] || T.en).rp_mon;
  const monLabel = (ym) => (lang === 'vi' ? `${ym.slice(5, 7)}/${ym.slice(0, 4)}` : `${mon[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`);
  const firstYm = useMemo(() => (data?.months || []).reduce((a, m) => (m.ym < a ? m.ym : a), thisYm), [data, thisYm]);
  const hasLog = useMemo(() => new Set((data?.months || []).map((m) => m.ym)), [data]);
  const years = useMemo(() => { const out = []; for (let y = Number(firstYm.slice(0, 4)); y <= Number(thisYm.slice(0, 4)); y += 1) out.push(y); return out; }, [firstYm, thisYm]);
  const months = useMemo(() => [...picked].filter((ym) => ym <= thisYm).sort(), [picked, thisYm]);
  const cars = useMemo(() => (data?.cars || []).filter((c) => (carSel ? c.vehicleId === carSel : (c.active || (data.months || []).some((m) => m.vehicleId === c.vehicleId && picked.has(m.ym))))),
    [data, carSel, picked]);
  const rowsOf = useCallback((car) => months.map((ym) => {
    const m = (data?.months || []).find((x) => x.vehicleId === car.vehicleId && x.ym === ym);
    return { ym, days: m?.days || 0, km: m?.km || 0, fuel: m?.fuel || 0, gas: data?.gas?.[ym] || null };
  }), [data, months]);
  const per = (fuel, km) => (km ? Math.round(fuel / km) : null);
  const setRange = (list) => setPicked(new Set(list.filter((ym) => ym <= thisYm)));
  const toggle = (ym) => setPicked((p) => { const n = new Set(p); if (n.has(ym)) n.delete(ym); else n.add(ym); return n; });
  const ymOf = (y, i) => `${y}-${String(i + 1).padStart(2, '0')}`;
  const period = months.length ? (months.length === 1 ? monLabel(months[0]) : `${monLabel(months[0])} – ${monLabel(months[months.length - 1])}`) : '';
  const chart = useMemo(() => months.map((ym) => {
    const row = { ym: monLabel(ym), gas: data?.gas?.[ym]?.price ?? null };
    cars.forEach((c) => { const m = (data?.months || []).find((x) => x.vehicleId === c.vehicleId && x.ym === ym); row[c.vehicleId] = m ? per(m.fuel, m.km) : null; });
    return row;
  }), [months, cars, data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = () => {
    const q = new URLSearchParams({ months: months.join(','), cars: cars.map((c) => c.vehicleId).join(',') });
    window.location.href = `${API}/admin/report.pdf?${q}`;
  };
  if (err) return <div className="pa-banner err">{err}</div>;
  if (!data) return <Loader2 size={16} className="pa-spin" />;
  const quick = [
    ['rp_last', [lastYm]], ['rp_l3', range(addMonths(lastYm, -2), 3)], ['rp_l6', range(addMonths(lastYm, -5), 6)], ['rp_l12', range(addMonths(lastYm, -11), 12)],
    ['rp_h1', range(`${year}-01`, 6), year], ['rp_h2', range(`${year}-07`, 6), year], ['rp_year', range(`${year}-01`, 12), year],
  ];
  const same = (list) => { const l = list.filter((ym) => ym <= thisYm); return l.length === months.length && l.every((ym) => picked.has(ym)); };
  return (
    <div className="dl-report" data-testid="dl-report">
      <div className="pa-section dl-editor">
        <h3>{t('rp_title')}</h3>
        <p className="dl-note">{t('rp_hint')}</p>
        <div className="dl-rp-quick" data-testid="dl-rp-quick">
          <span className="dl-rp-lbl">{t('rp_quick')}</span>
          {quick.map(([k, list, y]) => (
            <button key={k} type="button" className={`dl-rp-chip ${same(list) ? 'on' : ''}`} onClick={() => setRange(list)} data-rp-quick={k}>{t(k, y)}</button>
          ))}
        </div>
        <div className="dl-rp-pick">
          <div className="dl-rp-year">
            <button type="button" className="pa-btn" disabled={year <= years[0]} onClick={() => setYear(year - 1)} aria-label="previous year"><ChevronLeft size={14} /></button>
            <b data-testid="dl-rp-year">{year}</b>
            <button type="button" className="pa-btn" disabled={year >= years[years.length - 1]} onClick={() => setYear(year + 1)} aria-label="next year"><ChevronRight size={14} /></button>
          </div>
          <div className="dl-rp-months">
            {mon.map((label, i) => {
              const ym = ymOf(year, i);
              const future = ym > thisYm;
              return (
                <label key={ym} className={`dl-rp-mon ${picked.has(ym) ? 'on' : ''} ${!hasLog.has(ym) ? 'empty' : ''} ${future ? 'future' : ''}`}
                  title={!hasLog.has(ym) && !future ? t('rp_nolog') : undefined}>
                  <input type="checkbox" checked={picked.has(ym)} disabled={future} onChange={() => toggle(ym)} data-rp-month={ym} />{label}
                </label>
              );
            })}
          </div>
          <div className="dl-rp-count">
            <span data-testid="dl-rp-count">{t('rp_picked', months.length)}{period ? ` · ${period}` : ''}</span>
            {months.length > 0 && <button type="button" className="pa-btn" onClick={() => setPicked(new Set())}>{t('rp_clear')}</button>}
          </div>
        </div>
        <div className="dl-toolbar">
          <select value={carSel} onChange={(e) => setCarSel(e.target.value)} data-testid="dl-rp-car">
            <option value="">{t('rp_all_cars')}</option>
            {data.cars.map((c) => <option key={c.vehicleId} value={c.vehicleId}>{c.plate}{c.driver ? ` · ${c.driver}` : ''}</option>)}
          </select>
          <button type="button" className="pa-btn submit" disabled={!months.length} onClick={pdf} data-testid="dl-rp-pdf"><Download size={14} /> {t('rp_pdf')}</button>
        </div>
      </div>
      {!months.length ? <div className="pa-empty">{t('rp_pick_some')}</div> : (
        <>
          <div className="dl-rp-cards">
            {cars.map((c, i) => {
              const r = rowsOf(c); const km = r.reduce((a, x) => a + x.km, 0); const fuel = r.reduce((a, x) => a + x.fuel, 0);
              return (
                <div key={c.vehicleId} className="dl-rp-card" style={{ borderTopColor: RP_COLORS[i % RP_COLORS.length] }} data-rp-card={c.vehicleId}>
                  <div className="dl-rp-cardhead">{t('rp_car', c.plate, c.driver || '—')}</div>
                  <div className="dl-rp-cardnums">
                    <div><span>{t('rp_km')}</span><b>{n0(km)}</b></div>
                    <div><span>{t('rp_fuel')}</span><b>{n0(fuel)} ₫</b></div>
                    <div><span>{t('rp_vpk')}</span><b>{per(fuel, km) === null ? '—' : `${n0(per(fuel, km))} ₫`}</b></div>
                  </div>
                </div>
              );
            })}
          </div>
          {months.length > 1 && (
            <div className="pa-section dl-rp-chart">
              <h3>{t('rp_chart')}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} width={50} tickFormatter={(v) => n0(v)} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => n0(v)} />
                  <Tooltip formatter={(v) => (v === null || v === undefined ? '—' : `${n0(v)} ₫`)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {cars.map((c, i) => <Bar key={c.vehicleId} yAxisId="l" dataKey={c.vehicleId} name={`${t('rp_vpk')} · ${c.plate}`} fill={RP_COLORS[i % RP_COLORS.length]} isAnimationActive={false} />)}
                  <Line yAxisId="r" dataKey="gas" name={t('rp_gas')} stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          {cars.map((c) => {
            const r = rowsOf(c); const km = r.reduce((a, x) => a + x.km, 0); const fuel = r.reduce((a, x) => a + x.fuel, 0);
            return (
              <div key={c.vehicleId} className="pa-section">
                <h3 className="dl-rp-cartitle">{t('rp_car', c.plate, c.driver || '—')}<small>{t('rp_period', period)}</small></h3>
                <div className="dl-rp-wrap">
                  <table className="sp-table dl-rp-table" data-rp-table={c.vehicleId}>
                    <thead><tr>
                      <th>{t('rp_month')}</th><th className="num">{t('days_logged')}</th><th className="num">{t('rp_km')}</th><th className="num">{t('rp_fuel')}</th>
                      <th className="num">{t('rp_vpk')}</th><th className="num">{t('rp_gas')}</th><th className="num">{t('rp_gas_date')}</th>
                    </tr></thead>
                    <tbody>
                      {r.map((x) => (
                        <tr key={x.ym} className={x.days ? '' : 'dl-rp-none'}>
                          <td>{monLabel(x.ym)}</td><td className="num">{x.days || '—'}</td><td className="num">{x.days ? n0(x.km) : '—'}</td>
                          <td className="num">{x.days ? `${n0(x.fuel)} ₫` : '—'}</td><td className="num">{per(x.fuel, x.km) === null ? '—' : `${n0(per(x.fuel, x.km))} ₫`}</td>
                          <td className="num" title={x.gas?.typed ? t('rp_typed') : undefined}>{x.gas ? `${n0(x.gas.price)} ₫` : '—'}</td>
                          <td className="num">{x.gas?.refDate ? dmy(x.gas.refDate) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr>
                      <td>{t('rp_total')}</td><td className="num">{r.reduce((a, x) => a + x.days, 0)}</td><td className="num">{n0(km)}</td>
                      <td className="num">{n0(fuel)} ₫</td><td className="num">{per(fuel, km) === null ? '—' : `${n0(per(fuel, km))} ₫`}</td><td /><td />
                    </tr></tfoot>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const TABS = ['overview', 'entry', 'places', 'people', 'gas', 'close', 'report'];

/* THE GUIDE (Max 01/11: every module has one, ending on the manual). Admin screen only — the driver's phone view has none.
   It walks the Overview tab, so it first switches back to it; the cars and the month's table are drawn once the
   overview has loaded, and a step whose control is not there yet is skipped. */
const TOUR = [
  { selector: '[data-testid=dl-tabs]', title: 'tourDriverLog.tabsT', body: 'tourDriverLog.tabsB' },
  { selector: '.dl-admin .dl-toolbar', title: 'tourDriverLog.monthT', body: 'tourDriverLog.monthB' },
  { selector: '[data-testid=dl-ov-cars]', title: 'tourDriverLog.carsT', body: 'tourDriverLog.carsB' },
  { selector: '[data-testid=dl-ov-month-table]', title: 'tourDriverLog.daysT', body: 'tourDriverLog.daysB' },
  { selector: '.dl-carhead .pa-btn', title: 'tourDriverLog.editT', body: 'tourDriverLog.editB' },
  { selector: '[data-testid=dl-preview]', title: 'tourDriverLog.previewT', body: 'tourDriverLog.previewB' },
];

function AdminScreen({ me, lang, viLang }) {
  const { t, w } = lang;
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'overview';
  const setTab = (k) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', k); return n; });
  const [car, setCar] = useState(me.vehicles.find((v) => v.active)?.vehicle_id || me.vehicles[0]?.vehicle_id || '');
  const [date, setDate] = useState(me.today);
  const [preview, setPreview] = useState(false);
  const [ovReady, setOvReady] = useState(false);
  const carRow = me.vehicles.find((v) => v.vehicle_id === car);
  if (preview && carRow) {
    return <DriverScreen me={me} vehicleId={car} plate={carRow.plate} driverName={carRow.driver_name} lang={viLang} barLang={lang}
      onExit={() => setPreview(false)} cars={me.vehicles} onCar={setCar} />;
  }
  const edit = (vehicleId, d) => { setCar(vehicleId); if (d) setDate(d); setTab('entry'); };
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main dl-admin">
        <div className="pa-title dl-title">
          <div><h2>{t('title')} · {t('admin')}</h2></div>
          <div className="dl-title-acts">
            <button type="button" className="pa-btn" onClick={() => setPreview(true)} disabled={!carRow} data-testid="dl-preview"><Eye size={14} /> {t('preview_btn')}</button>
            <ModuleHelp manual="driverlog" steps={TOUR} tourKey="driverLog" ready={tab !== 'overview' || ovReady} onBeforeTour={() => setTab('overview')} />
          </div>
        </div>
        <div className="pa-tabs view-tabs" role="tablist" data-testid="dl-tabs">
          {TABS.map((k) => <button key={k} type="button" className={`pa-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}><TabIcon k={k} />{t(`tab_${k}`)}</button>)}
        </div>
        {tab === 'overview' && <Overview t={t} w={w} today={me.today} onEdit={edit} onLoaded={setOvReady} />}
        {tab === 'entry' && <Entry t={t} w={w} me={me} car={car} setCar={setCar} date={date} setDate={setDate} />}
        {tab === 'places' && <Places t={t} />}
        {tab === 'people' && <People t={t} />}
        {tab === 'gas' && <Gas t={t} w={w} today={me.today} />}
        {tab === 'close' && <Close t={t} today={me.today} startMonth={me.startMonth} />}
        {tab === 'report' && <Report t={t} lang={lang.lang} today={me.today} />}
      </div>
    </div>
  );
}

export default function DriverLog() {
  const sysLang = useLang(false);
  const viLang = useLang(true);
  const { driverOnly } = useAuth();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { call(`${API}/me`).then(setMe).catch((e) => setErr(e.message)); }, []);
  const view = useMemo(() => {
    if (!me) return null;
    if (me.isAdmin) return 'admin';
    return me.driver && me.driver.vehicle_id ? 'driver' : 'nocar';
  }, [me]);
  useLightRoot(driverOnly);
  const phone = `dl-phone${driverOnly ? ' dl-light' : ''}`;
  if (err) return <div className={phone}><div className="dl-msg err" style={{ margin: 16 }}>{err}</div></div>;
  if (!me) return <div className={phone}><div className="dl-loading"><Loader2 size={20} className="pa-spin" /> {(driverOnly ? viLang : sysLang).t('loading')}</div></div>;
  if (view === 'admin') return <AdminScreen me={me} lang={sysLang} viLang={viLang} />;
  if (view === 'nocar') return <div className={phone}><div className="dl-msg warn" style={{ margin: 16 }}>{viLang.t('no_car')}</div></div>;
  return <DriverScreen me={me} vehicleId={me.driver.vehicle_id} plate={me.driver.plate} driverName={me.driver.name} lang={viLang} />;
}
